import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import { API_BASE } from './config';
import socket from '../socket/socket';
import '../css/RandomMatchPanel.css';

const MODES = [
  {
    value: 'PICK_ONE',
    label: 'Pick One',
    description: 'ล็อกผู้เล่นหนึ่งคนแล้วหาอีกสามคน'
  },
  {
    value: 'AI_RANDOM',
    label: 'AI RANDOM',
    description: 'มาก่อนได้ก่อน เลือกคะแนนรายบุคคลใกล้กัน แล้วจัดทีมให้สูสี'
  },
  {
    value: 'MANUAL_4',
    label: 'Select 4',
    description: 'เลือก Team 1 สองคน และ Team 2 สองคน'
  }
];

const AUTOMATIC_MODES = new Set(['AI_RANDOM']);
const RECOMMENDATION_VISIBLE_STORAGE_KEY =
  'badminton-match-recommendation-visible';

const POINT_RANGES = [100, 200, 300];
const RANK_ORDER = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const PICK_ONE_STATUS_ORDER = ['ACTIVE', 'QUEUE', 'PLAY', 'HOLD', 'CHECKOUT'];

const PLAY_STYLE_OPTIONS = [
  {
    value: 'ANY',
    icon: '🏸',
    label: 'ทุก Mood',
    description: 'ไม่กรอง Mood ของวันนี้'
  },
  {
    value: 'FAST_POWER_MIX',
    icon: '⚡',
    label: 'ทั่วไป',
    description: 'เร็ว หนัก สลับ เบา'
  },
  {
    value: 'TIGHT_MEDIUM_HEAVY',
    icon: '🎯',
    label: 'ขอเหนื่อย',
    description: 'ตึง ปานกลาง–หนัก'
  },
  {
    value: 'ALL_ROUND_READY',
    icon: '🔥',
    label: 'ได้หมด พร้อมชน',
    description: 'พร้อมเล่นทุกรูปแบบ'
  },
  {
    value: 'ENJOY_FUN',
    icon: '😄',
    label: 'เน้นฮาและเหนื่อย',
    description: 'Enjoy เฮฮา ไม่เครียด'
  }
];

function getPlayStyleLabel(value) {
  return PLAY_STYLE_OPTIONS.find(option => option.value === value)?.label || '-';
}

const PLAY_STYLE_ALIASES = new Map([
  ['FAST_POWER_MIX', 'FAST_POWER_MIX'],
  ['เน้นเร็ว หนัก สลับ เบา (ตามระดับมือ)', 'FAST_POWER_MIX'],
  ['เร็ว หนัก สลับ เบา', 'FAST_POWER_MIX'],
  ['TIGHT_MEDIUM_HEAVY', 'TIGHT_MEDIUM_HEAVY'],
  ['เน้นตึง ปานกลาง–หนัก (ตามระดับมือ)', 'TIGHT_MEDIUM_HEAVY'],
  ['เน้นตึง ปานกลาง - หนัก (ตามระดับมือ)', 'TIGHT_MEDIUM_HEAVY'],
  ['ตึง ปานกลาง–หนัก', 'TIGHT_MEDIUM_HEAVY'],
  ['ALL_ROUND_READY', 'ALL_ROUND_READY'],
  ['ได้หมด พร้อมชน', 'ALL_ROUND_READY'],
  ['ENJOY_FUN', 'ENJOY_FUN'],
  ['เน้น Enjoy เฮฮา ไม่เครียด (ตามระดับมือ)', 'ENJOY_FUN'],
  ['Enjoy เฮฮา', 'ENJOY_FUN']
]);

function normalizePlayStyleValue(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return null;

  return (
    PLAY_STYLE_ALIASES.get(rawValue) ||
    PLAY_STYLE_ALIASES.get(rawValue.toUpperCase()) ||
    null
  );
}

function getStatus(player) {
  return String(player?.memberStatus ?? player?.status ?? 'ACTIVE').toUpperCase();
}
function getQueueMatchNo(player) {

  const value =
    player?.queueMatchNo ??
    player?.queue_match_no;

  const matchNo =
    Number(value);

  return Number.isFinite(matchNo) &&
    matchNo > 0
    ? matchNo
    : null;
} function getQueueMatchId(player) {

  const value =
    player?.queueMatchId ??
    player?.queue_match_id;

  const matchId =
    Number(value);

  return Number.isFinite(matchId) &&
    matchId > 0
    ? matchId
    : null;
}
function getPlayingMatchNo(player) {
  const value =
    player?.playingMatchNo ??
    player?.playingGroupMatchNo ??
    player?.playing_match_no;
  const matchNo = Number(value);
  return Number.isFinite(matchNo) && matchNo > 0 ? matchNo : null;
}

function getPlayingMatchId(player) {
  const value = player?.playingMatchId ?? player?.playing_match_id;
  const matchId = Number(value);
  return Number.isFinite(matchId) && matchId > 0 ? matchId : null;
}
function getPickOneStatusPriority(player) {
  const index = PICK_ONE_STATUS_ORDER.indexOf(getStatus(player));
  return index === -1 ? PICK_ONE_STATUS_ORDER.length : index;
}

function getPlayerGames(player) {
  return Number(
    player?.totalMatches ??
    player?.total_matches ??
    player?.total_games ??
    0
  ) || 0;
}

function normalizePlayerRank(value, point = 0) {
  const rawRank = String(value || '').trim();
  const rankWithoutPrefix = rawRank.startsWith('มือ')
    ? rawRank.slice('มือ'.length).trim()
    : rawRank;
  const normalized = rankWithoutPrefix
    .toUpperCase()
    .replace(/^SP$/, 'S/P')
    .replace(/^S-P$/, 'S/P')
    .replace(/^NP$/, 'N/S')
    .replace(/^N-S$/, 'N/S');

  if (RANK_ORDER.includes(normalized)) return normalized;

  const safePoint = Number(point || 0);
  if (safePoint < 350) return 'BG';
  if (safePoint < 400) return 'BGM';
  if (safePoint < 450) return 'NB';
  if (safePoint < 500) return 'NB+';
  if (safePoint < 550) return 'N-';
  if (safePoint < 600) return 'N';
  if (safePoint < 650) return 'N/S';
  if (safePoint < 700) return 'S';
  return 'S/P';
}

function getRankDistance(left, right) {
  const leftRank = normalizePlayerRank(left?.level_rank, left?.point);
  const rightRank = normalizePlayerRank(right?.level_rank, right?.point);
  return Math.abs(RANK_ORDER.indexOf(leftRank) - RANK_ORDER.indexOf(rightRank));
}

function parsePlayerTime(value) {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function getQueueStartedAt(player) {
  if (getStatus(player) === 'QUEUE') {
    return parsePlayerTime(
      player?.queueCreatedAt ??
      player?.queue_created_at ??
      player?.queueStartTime
    );
  }

  if (getStatus(player) === 'PLAY') {
    return parsePlayerTime(
      player?.playingStartTime ??
      player?.playing_start_time ??
      player?.start_time
    );
  }

  return parsePlayerTime(
    player?.last_finished_at ||
    player?.active_since ||
    player?.checkin_time
  );
}

function getPlayerMatchStatusLabel(player) {
  const status = getStatus(player);
  if (status === 'QUEUE') {
    const matchNo = getQueueMatchNo(player) || getQueueMatchId(player);
    return matchNo ? `Queue Match #${matchNo}` : 'อยู่ Match Queue';
  }
  if (status === 'PLAY') {
    const matchNo = getPlayingMatchNo(player) || getPlayingMatchId(player);
    return matchNo ? `PLAY Match #${matchNo}` : 'กำลัง PLAY';
  }
  return status;
}

function getArrivalTime(player) {
  return parsePlayerTime(
    player?.active_since ||
    player?.checkin_time ||
    player?.last_finished_at
  );
}

function comparePlayersByPriority(left, right, priority) {
  if (priority === 'LEAST_GAMES') {
    const gameDiff = getPlayerGames(left) - getPlayerGames(right);
    if (gameDiff !== 0) return gameDiff;
  }

  if (priority === 'WAIT_LONGEST') {
    const waitDiff = getQueueStartedAt(left) - getQueueStartedAt(right);
    if (waitDiff !== 0) return waitDiff;
  }

  const arrivalDiff = getArrivalTime(left) - getArrivalTime(right);
  if (arrivalDiff !== 0) return arrivalDiff;

  return Number(left.user_id) - Number(right.user_id);
}

function formatWaitTime(player, now) {
  const startedAt = getQueueStartedAt(player);
  if (startedAt === Number.MAX_SAFE_INTEGER) return '-';

  const minutes = Math.max(0, Math.floor((now - startedAt) / 60000));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) return `${hours} ชม. ${remainingMinutes} นาที`;
  return `${minutes} นาที`;
}

function getRelationCount(relationMap, leftPlayer, rightPlayer) {
  const leftId = Number(leftPlayer?.user_id);
  const rightId = Number(rightPlayer?.user_id);

  if (!Number.isInteger(leftId) || !Number.isInteger(rightId)) return 0;

  return Number(
    relationMap?.[`${leftId}-${rightId}`] ??
    relationMap?.[`${rightId}-${leftId}`] ??
    0
  ) || 0;
}

function normalizeDropdownPlayer(player) {
  if (!player) return player;

  let partners = [];
  if (Array.isArray(player.partners)) {
    partners = player.partners;
  } else if (typeof player.partners === 'string') {
    partners = player.partners
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  } else if (player.partners && typeof player.partners === 'object') {
    partners = Object.values(player.partners)
      .filter(Boolean)
      .map(item => String(item).trim())
      .filter(Boolean);
  }

  return { ...player, partners };
}

function normalizeDropdownPlayers(players) {
  return Array.isArray(players)
    ? players.map(normalizeDropdownPlayer)
    : [];
}

async function postMatchmakingJson(path, payload) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    const requestError = new Error(
      responseData.message ||
      responseData.error ||
      `HTTP ${response.status}`
    );
    requestError.data = responseData;
    requestError.status = response.status;
    throw requestError;
  }

  return responseData;
}

function RandomMatchPanel({
  groupId,
  allPlayers = [],
  lockedPairs = [],
  pairMap = {},
  opponentMap = {},
  onCreated,
  displayMode = 'full'
}) {
  const isMonitorAi = displayMode === 'monitor-ai';
  const [mode, setMode] = useState('PICK_ONE');
  const [priority, setPriority] = useState('WAIT_LONGEST');
  const [pointRange, setPointRange] = useState(100);
  const [playStyle, setPlayStyle] = useState('ANY');
  const [matchCount, setMatchCount] = useState(1);
  const [approvePlaying, setApprovePlaying] = useState(false);
  const [priorityKeyword, setPriorityKeyword] = useState('');
  const [pickedPlayer, setPickedPlayer] = useState(null);
  const [secondPickedPlayer, setSecondPickedPlayer] = useState(null);
  const [pickOnePlayers, setPickOnePlayers] = useState([]);
  const [manualPlayers, setManualPlayers] = useState([
    null,
    null,
    null,
    null
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState('ACTIVE_RANK');
  const [previewMatches, setPreviewMatches] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCreatingIndex, setPreviewCreatingIndex] = useState(null);
  const [previewAlert, setPreviewAlert] = useState(null);
  const [previewHistoryByUserId, setPreviewHistoryByUserId] = useState({});
  const [editingPreviewSlot, setEditingPreviewSlot] = useState(null);
  const [previewEditSearch, setPreviewEditSearch] = useState('');
  const [previewManuallyEdited, setPreviewManuallyEdited] = useState(false);
  const [selectedPreviewRanks, setSelectedPreviewRanks] = useState([]);
  const [waitingAlertLimit, setWaitingAlertLimit] = useState(5);
  const [waitingAlertVisible, setWaitingAlertVisible] = useState(true);
  const [previewRealtimeVersion, setPreviewRealtimeVersion] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [dailyStyleByUserId, setDailyStyleByUserId] = useState({});
  const [aiAutoEnabled, setAiAutoEnabled] = useState(false);
  const [aiAutoLoading, setAiAutoLoading] = useState(false);
  const [aiAutomationStatus, setAiAutomationStatus] = useState('');
  const [, setAiRecommendation] = useState(null);
  const [aiAutomationCycle, setAiAutomationCycle] = useState(0);
  const [expandedInfoPanel, setExpandedInfoPanel] = useState(null);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [statusSearchKeyword, setStatusSearchKeyword] = useState('');
  const [nearbySearchKeyword, setNearbySearchKeyword] = useState('');
  const [nearbyGameSort, setNearbyGameSort] = useState('ASC');
  const [selectedNearbyRanks, setSelectedNearbyRanks] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState(null);
  const [holdManagerOpen, setHoldManagerOpen] = useState(false);
  const [recommendationVisible] = useState(() => {
    try {
      return window.localStorage.getItem(
        RECOMMENDATION_VISIBLE_STORAGE_KEY
      ) !== 'false';
    } catch {
      return true;
    }
  });
  const aiRequestInFlightRef = useRef(false);
  const aiProcessedSignatureRef = useRef('');
  const pickOneAttemptedIdsRef = useRef(new Set());
  const onCreatedRef = useRef(onCreated);
  const previewRealtimeHandledRef = useRef(0);

  const canApprovePlaying = mode === 'PICK_ONE' || mode === 'MANUAL_4';
  const usesManualSelection = mode === 'MANUAL_4';
  const supportsMultipleMatches = mode === 'AI_RANDOM';
  const usesIndividualPointRange = mode === 'PICK_ONE';
  const usesPointRange = usesIndividualPointRange;

  useEffect(() => {
    onCreatedRef.current = onCreated;
  }, [onCreated]);

  useEffect(() => {
    if (!previewOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [previewOpen]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        RECOMMENDATION_VISIBLE_STORAGE_KEY,
        String(recommendationVisible)
      );
    } catch {
      // หาก localStorage ใช้ไม่ได้ ปุ่มยังเปิด/ปิดได้ในรอบปัจจุบัน
    }
  }, [recommendationVisible]);

  const loadDailyStyles = useCallback(async () => {
    if (!groupId) {
      setDailyStyleByUserId({});
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/users/daily-play-style/${groupId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      const nextStyleMap = {};

      rows.forEach(row => {
        const userId = Number(row.user_id);
        const style = normalizePlayStyleValue(
          row.daily_play_style ?? row.play_style
        );

        if (Number.isInteger(userId) && userId > 0 && style) {
          nextStyleMap[userId] = style;
        }
      });

      setDailyStyleByUserId(nextStyleMap);
    } catch (loadError) {
      console.error('LOAD DAILY PLAY STYLES ERROR:', loadError);
    }
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return undefined;

    const isCurrentGroupEvent = (payload = {}) => {
      const eventGroupId =
        payload.groupId ??
        payload.group_id;

      // ถ้า Backend ส่ง groupId มา ให้ตรวจว่าเป็น Group ปัจจุบัน
      if (eventGroupId !== undefined && eventGroupId !== null) {
        return Number(eventGroupId) === Number(groupId);
      }

      // รองรับกรณี Backend ส่ง groupIds เป็น array
      const eventGroupIds = Array.isArray(payload.groupIds)
        ? payload.groupIds.map(Number)
        : [];

      // ถ้า event ไม่มี groupId ให้ refresh Group ปัจจุบัน
      return (
        eventGroupIds.length === 0 ||
        eventGroupIds.includes(Number(groupId))
      );
    };

    const refreshPlayers = async (payload = {}) => {
      if (!isCurrentGroupEvent(payload)) return;
      setPreviewRealtimeVersion(current => current + 1);

      // โหลด Mood ใหม่
      loadDailyStyles().catch(error => {
        console.error(
          'Realtime daily style refresh error:',
          error
        );
      });

      // เรียก Parent ให้โหลด allPlayers ใหม่
      if (onCreatedRef.current) {
        try {
          await onCreatedRef.current(payload);
        } catch (error) {
          console.error(
            'Realtime players refresh error:',
            error
          );
        }
      }
    };

    const joinGroupAndRefresh = () => {
      console.log(
        '🔌 MATCHMAKING JOIN GROUP:',
        Number(groupId)
      );

      // สำคัญ: ต้องเข้า Socket room ของ Group
      socket.emit(
        'join-group',
        Number(groupId)
      );

      // โหลดข้อมูลทันทีหลัง connect
      loadDailyStyles().catch(error => {
        console.error(
          'Initial daily style refresh error:',
          error
        );
      });

      if (onCreatedRef.current) {
        Promise.resolve(
          onCreatedRef.current()
        ).catch(error => {
          console.error(
            'Initial players refresh error:',
            error
          );
        });
      }
    };

    // Socket reconnect
    socket.on(
      'connect',
      joinGroupAndRefresh
    );

    // ถ้า Socket connect อยู่แล้ว
    if (socket.connected) {
      joinGroupAndRefresh();
    }

    // =====================================
    // REALTIME PLAYER EVENTS
    // =====================================

    socket.on(
      'player-added',
      refreshPlayers
    );

    socket.on(
      'member-joined',
      refreshPlayers
    );

    socket.on(
      'member-updated',
      refreshPlayers
    );

    socket.on(
      'player-style-updated',
      refreshPlayers
    );

    // สำคัญสำหรับ ACTIVE / QUEUE / PLAY
    socket.on(
      'player-status-updated',
      refreshPlayers
    );

    // สำคัญสำหรับข้อมูลผู้เล่นที่เปลี่ยน
    socket.on(
      'player-updated',
      refreshPlayers
    );

    socket.on('queue-updated', refreshPlayers);
    socket.on('match-generated', refreshPlayers);
    socket.on('match-updated', refreshPlayers);
    socket.on('match-started', refreshPlayers);
    socket.on('match-ended', refreshPlayers);

    // =====================================
    // POLLING BACKUP
    // =====================================

    const pollingId = window.setInterval(
      () => {
        loadDailyStyles().catch(() => { });

        if (onCreatedRef.current) {
          Promise.resolve(
            onCreatedRef.current()
          ).catch(() => { });
        }
      },
      5000
    );

    // เมื่อกลับมาหน้า Browser
    const handleFocus = () => {
      socket.emit(
        'join-group',
        Number(groupId)
      );

      refreshPlayers({
        groupId: Number(groupId)
      });
    };

    window.addEventListener(
      'focus',
      handleFocus
    );

    return () => {
      window.clearInterval(pollingId);

      window.removeEventListener(
        'focus',
        handleFocus
      );

      socket.off(
        'connect',
        joinGroupAndRefresh
      );

      socket.off(
        'player-added',
        refreshPlayers
      );

      socket.off(
        'member-joined',
        refreshPlayers
      );

      socket.off(
        'member-updated',
        refreshPlayers
      );

      socket.off(
        'player-style-updated',
        refreshPlayers
      );

      socket.off(
        'player-status-updated',
        refreshPlayers
      );

      socket.off(
        'player-updated',
        refreshPlayers
      );

      socket.off('queue-updated', refreshPlayers);
      socket.off('match-generated', refreshPlayers);
      socket.off('match-updated', refreshPlayers);
      socket.off('match-started', refreshPlayers);
      socket.off('match-ended', refreshPlayers);
    };
  }, [
    groupId,
    loadDailyStyles
  ]);

  const normalizedPlayers = useMemo(
    () => allPlayers.map(player => {

      const userId =
        Number(player.user_id);

      const dailyPlayStyle =
        normalizePlayStyleValue(
          dailyStyleByUserId[userId] ??
          player.daily_play_style ??
          player.play_style ??
          player.dailyPlayStyle
        );


      /* =================================================
         NORMALIZE PARTNERS
         Backend บางครั้งส่ง Array
         บางครั้งส่ง string จาก GROUP_CONCAT
      ================================================= */

      let partners = [];

      if (Array.isArray(player.partners)) {

        partners =
          player.partners
            .filter(Boolean)
            .map(item =>
              String(item).trim()
            )
            .filter(Boolean);

      } else if (
        typeof player.partners === 'string'
      ) {

        partners =
          player.partners
            .split(',')
            .map(item =>
              item.trim()
            )
            .filter(Boolean);

      } else {

        partners = [];

      }


      return normalizeDropdownPlayer({
        ...player,

        partners,

        daily_play_style:
          dailyPlayStyle,

        status:
          getStatus(player),

        memberStatus:
          getStatus(player)
      });

    }),

    [
      allPlayers,
      dailyStyleByUserId
    ]
  );

  useEffect(() => {
    if (
      !previewOpen ||
      previewMatches.length === 0 ||
      previewManuallyEdited
    ) return;

    const latestPlayerById = new Map(
      normalizedPlayers.map(player => [Number(player.user_id), player])
    );
    const nextMatches = previewMatches.filter(match =>
      [...(match.team1 || []), ...(match.team2 || [])].every(previewPlayer => {
        const latestPlayer = latestPlayerById.get(Number(previewPlayer.user_id));
        if (!latestPlayer || getStatus(latestPlayer) === 'HOLD') return false;
        if (getQueueMatchId(latestPlayer)) return false;
        return previewType !== 'ACTIVE_RANK' ||
          ['ACTIVE', 'PLAY'].includes(getStatus(latestPlayer));
      })
    );

    if (nextMatches.length !== previewMatches.length) {
      setPreviewMatches(nextMatches);
      setPreviewAlert({
        type: 'info',
        text: 'อัปเดต Preview แล้ว: นำ Match ที่มีผู้เล่นซึ่งถูกจัด Queue ออก'
      });
    }
  }, [
    normalizedPlayers,
    previewMatches,
    previewOpen,
    previewType,
    previewManuallyEdited
  ]);

  const activeStyleCounts = useMemo(() => {
    const counts = {
      FAST_POWER_MIX: 0,
      TIGHT_MEDIUM_HEAVY: 0,
      ALL_ROUND_READY: 0,
      ENJOY_FUN: 0
    };
    let totalActive = 0;
    let missingStyle = 0;

    normalizedPlayers.forEach(player => {
      if (getStatus(player) !== 'ACTIVE') return;
      totalActive += 1;

      if (player.daily_play_style && counts[player.daily_play_style] != null) {
        counts[player.daily_play_style] += 1;
      } else {
        missingStyle += 1;
      }
    });

    return { counts, totalActive, missingStyle };
  }, [normalizedPlayers]);

  const minimumStylePlayers = supportsMultipleMatches
    ? matchCount * 4
    : 4;

  const styleMatchingPlayers = useMemo(
    () => playStyle === 'ANY'
      ? normalizedPlayers
      : normalizedPlayers.filter(
        player => player.daily_play_style === playStyle
      ),
    [normalizedPlayers, playStyle]
  );

  const styleMatchingActiveCount = useMemo(
    () => styleMatchingPlayers.filter(
      player => getStatus(player) === 'ACTIVE'
    ).length,
    [styleMatchingPlayers]
  );

  const styleEligiblePlayers = useMemo(
    () => (
      mode === 'AI_RANDOM' || playStyle === 'ANY'
        ? normalizedPlayers
        : styleMatchingPlayers
    ),
    [
      mode,
      normalizedPlayers,
      playStyle,
      styleMatchingPlayers
    ]
  );

  const activePlayers = useMemo(
    () => styleEligiblePlayers.filter(
      player => getStatus(player) === 'ACTIVE'
    ),
    [styleEligiblePlayers]
  );

  const aiOrderedActivePlayers = useMemo(
    () => normalizedPlayers
      .filter(player => getStatus(player) === 'ACTIVE')
      .sort((left, right) =>
        comparePlayersByPriority(left, right, 'FIFO')
      ),
    [normalizedPlayers]
  );

  const aiActiveSignature = useMemo(
    () => aiOrderedActivePlayers.map(player => [
      Number(player.user_id),
      Number(player.point || 0),
      player.level_rank || '',
      player.daily_play_style || '',
      player.active_since ||
      player.last_finished_at ||
      player.checkin_time ||
      ''
    ].join(':')).join('|'),
    [aiOrderedActivePlayers]
  );

  const aiLockSignature = useMemo(
    () => lockedPairs
      .map(lock => [
        Number(lock.user_a),
        Number(lock.user_b)
      ].sort((left, right) => left - right).join('-'))
      .sort()
      .join('|'),
    [lockedPairs]
  );

  const pickOneAutoSignature = mode === 'PICK_ONE'
    ? [
      Number(pickedPlayer?.user_id || 0),
      ...pickOnePlayers.map(player => Number(player.user_id)),
      pointRange,
      priority,
      approvePlaying ? 1 : 0
    ].join(':')
    : '';
  const aiMatchmakingSignature =
    `${mode}::${aiActiveSignature}::${aiLockSignature}::${matchCount}::${pickOneAutoSignature}`;

  const availableMatchCount = Math.min(
    15,
    Math.floor(activePlayers.length / 4)
  );
  const maxMatchCount =
    mode === 'AI_RANDOM'
      ? 15
      : Math.max(1, availableMatchCount);

  const requiredPlayerCount = supportsMultipleMatches
    ? matchCount * 4
    : 4;

  const playingPlayers = useMemo(
    () => styleEligiblePlayers.filter(
      player => getStatus(player) === 'PLAY'
    ),
    [styleEligiblePlayers]
  );

  const selectablePlayers = useMemo(() => {
    if (!canApprovePlaying || !approvePlaying) return activePlayers;

    // HOLD หมายถึงยังไม่พร้อม จึงไม่ถูกนำไปจัด Match แม้เปิด Approve
    return styleEligiblePlayers.filter(
      player => getStatus(player) !== 'HOLD'
    );
  }, [
    activePlayers,
    approvePlaying,
    canApprovePlaying,
    styleEligiblePlayers
  ]);

  const strictStyleAvailableCount =
    canApprovePlaying && approvePlaying
      ? selectablePlayers.length
      : activePlayers.length;
  const lacksRequiredStylePlayers =
    playStyle !== 'ANY' &&
    strictStyleAvailableCount < minimumStylePlayers;

  const orderedSelectablePlayers = useMemo(
    () => [...selectablePlayers].sort(
      (left, right) => (
        mode === 'PICK_ONE'
          ? getPickOneStatusPriority(left) - getPickOneStatusPriority(right) ||
          getPlayerGames(left) - getPlayerGames(right) ||
          comparePlayersByPriority(left, right, 'WAIT_LONGEST')
          : comparePlayersByPriority(left, right, priority)
      )
    ),
    [mode, priority, selectablePlayers]
  );

  /* =================================================
     PICK ONE ANCHOR
  
     ผู้เล่นหลักต้อง:
     - ACTIVE
     - ยังไม่อยู่ Match Queue
     - ไม่ใช่ PLAY
     - ไม่เกี่ยวกับ Approve
  
     Approve ใช้กับ "ผู้เล่นที่เลือกเพิ่ม" เท่านั้น
  ================================================= */

  const pickOneAnchorPlayers = useMemo(() => {

    return normalizedPlayers
      .filter(player => {

        const status = getStatus(player);

        const queueMatchNo =
          Number(
            player.queueMatchNo ??
            player.queue_match_no ??
            0
          );

        const queueMatchId =
          Number(
            player.queueMatchId ??
            player.queue_match_id ??
            0
          );

        return (
          status === 'ACTIVE' &&
          !queueMatchNo &&
          !queueMatchId
        );

      })
      .sort((left, right) => {

        const gameDiff =
          getPlayerGames(left) -
          getPlayerGames(right);

        if (gameDiff !== 0) {
          return gameDiff;
        }

        return comparePlayersByPriority(
          left,
          right,
          'WAIT_LONGEST'
        );

      });

  }, [normalizedPlayers]);


  /*
   * รายชื่อสำหรับเลือกเพิ่ม
   * ยังใช้ Approve ได้ตามเดิม
   */
  const pickOneSelectablePlayers = useMemo(
    () => orderedSelectablePlayers,
    [orderedSelectablePlayers]
  );
  function getAvatarSource(player) {

    const avatarUrl =
      player?.avatar_url ||
      player?.avatarUrl ||
      player?.avatar ||
      '';

    if (!avatarUrl) {
      return '';
    }

    const value =
      String(avatarUrl).trim();

    if (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('data:') ||
      value.startsWith('blob:')
    ) {
      return value;
    }

    return (
      `${API_BASE}` +
      `${value.startsWith('/') ? '' : '/'}` +
      value
    );
  }

  function PlayerAvatar({
    player,
    size = 'normal'
  }) {

    const avatarSource =
      getAvatarSource(player);

    const firstLetter =
      String(
        player?.name || '🏸'
      )
        .trim()
        .charAt(0)
        .toUpperCase();

    return (
      <span
        className={`rmp-player-avatar ${size}`}
      >

        {avatarSource ? (
          <img
            src={avatarSource}
            alt={player?.name || 'Player'}
            onError={event => {
              event.currentTarget.style.display =
                'none';

              const fallback =
                event.currentTarget.nextElementSibling;

              if (fallback) {
                fallback.style.display =
                  'grid';
              }
            }}
          />
        ) : null}

        <span
          className="rmp-player-avatar-fallback"
          style={{
            display:
              avatarSource
                ? 'none'
                : 'grid'
          }}
        >
          {firstLetter || '🏸'}
        </span>

      </span>
    );
  }
  const statusSearchResults = useMemo(() => {
    const keyword = statusSearchKeyword.trim().toLowerCase();
    if (!keyword) return [];

    return normalizedPlayers
      .filter(player =>
        String(player.name || '').toLowerCase().includes(keyword)
      )
      .sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      )
      .slice(0, 12);
  }, [normalizedPlayers, statusSearchKeyword]);

  const heldPlayers = useMemo(
    () => normalizedPlayers
      .filter(player => getStatus(player) === 'HOLD')
      .sort((left, right) =>
        String(left.name || '').localeCompare(String(right.name || ''), 'th')
      ),
    [normalizedPlayers]
  );

  const visibleStatusPlayers = statusSearchKeyword.trim()
    ? statusSearchResults
    : heldPlayers;

  const updateAvailabilityStatus = async (player, nextStatus) => {
    const userId = Number(player?.user_id);
    if (!Number.isInteger(userId) || statusUpdatingId) return;

    setStatusUpdatingId(userId);
    setStatusUpdateMessage(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/users/${userId}/group/${Number(groupId)}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: nextStatus })
        }
      );
      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP ${response.status}`);
      }

      setStatusUpdateMessage({
        type: 'success',
        text: `${player.name} เปลี่ยนเป็น ${nextStatus} แล้ว`
      });

      if (onCreatedRef.current) {
        await onCreatedRef.current(responseData);
      }
    } catch (statusError) {
      setStatusUpdateMessage({
        type: 'error',
        text: statusError.message || 'อัปเดตสถานะไม่สำเร็จ'
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // Pick One ใช้ผู้เล่นคนแรกของลิสต์เป็นค่าเริ่มต้นเสมอ เมื่อคนเดิม
  // เปลี่ยนเป็น QUEUE/PLAY หรือไม่อยู่ในเงื่อนไขที่เลือกได้ ระบบจะเลื่อนไปคนถัดไป
  /* =================================================
   PICK ONE AUTO ADVANCE
================================================= */

  useEffect(() => {

    if (mode !== 'PICK_ONE') {
      return;
    }

    const currentId =
      Number(pickedPlayer?.user_id || 0);


    /* =================================================
       เช็กว่าผู้เล่นหลักปัจจุบันยังเป็น Anchor ที่ถูกต้องไหม
    ================================================= */

    const currentPlayer =
      pickOneAnchorPlayers.find(
        player =>
          Number(player.user_id) === currentId
      );


    /* =================================================
       ถ้ายัง ACTIVE และไม่อยู่ Queue
       ให้ใช้คนเดิม
    ================================================= */

    if (currentPlayer) {

      /*
       * ตรวจรายชื่อที่เลือกเพิ่มด้วย
       */
      const selectableIds =
        new Set(
          pickOneSelectablePlayers.map(
            player =>
              Number(player.user_id)
          )
        );


      const validAdditionalPlayers =
        pickOnePlayers.filter(
          player =>
            selectableIds.has(
              Number(player.user_id)
            )
        );


      if (
        validAdditionalPlayers.length !==
        pickOnePlayers.length
      ) {

        setPickOnePlayers(
          validAdditionalPlayers
        );

        setError('');

        setAiRecommendation(null);

        aiProcessedSignatureRef.current = '';

      }


      return;
    }


    /* =================================================
       คนเดิมไม่พร้อมแล้ว
       เช่น ACTIVE -> QUEUE
       เลื่อนไปคนใหม่ทันที
    ================================================= */

    const nextPlayer =
      pickOneAnchorPlayers[0] || null;


    setPickedPlayer(
      nextPlayer
    );

    setPickOnePlayers([]);

    setNearbySearchKeyword('');

    setError('');

    setAiRecommendation(null);

    aiProcessedSignatureRef.current = '';

    pickOneAttemptedIdsRef.current.clear();


    setAiAutomationStatus(
      nextPlayer
        ? `✅ เลื่อนไปผู้เล่นหลักคนถัดไป: ${nextPlayer.name}`
        : 'กำลังรอผู้เล่น ACTIVE คนถัดไป'
    );

  }, [
    mode,
    pickedPlayer,
    pickOnePlayers,
    pickOneAnchorPlayers,
    pickOneSelectablePlayers
  ]);
  const priorityPlayers = useMemo(() => {
    const search = priorityKeyword.trim().toLowerCase();
    if (!search) return orderedSelectablePlayers;

    return orderedSelectablePlayers.filter(player => [
      player.name,
      player.point,
      player.level_rank,
      getPlayStyleLabel(player.daily_play_style),
      getPlayerGames(player)
    ]
      .filter(value => value !== null && value !== undefined)
      .join(' ')
      .toLowerCase()
      .includes(search));
  }, [orderedSelectablePlayers, priorityKeyword]);

  const selectedManualPlayers = manualPlayers.filter(Boolean);
  const selectedPriorityIdSet = new Set(
    selectedManualPlayers.map(player => Number(player.user_id))
  );
  const orderedPrioritySelections = orderedSelectablePlayers.filter(
    player => selectedPriorityIdSet.has(Number(player.user_id))
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setApprovePlaying(false);
    setPriority('WAIT_LONGEST');
    setPointRange(100);
    setPlayStyle('ANY');
    setMatchCount(1);
    setPriorityKeyword('');
    setPickedPlayer(null);
    setSecondPickedPlayer(null);
    setPickOnePlayers([]);
    setManualPlayers([null, null, null, null]);
    setLastResult(null);
    setError('');
    setAiAutoEnabled(false);
    setAiAutoLoading(false);
    setAiAutomationStatus('');
    setAiRecommendation(null);
    setAdvancedSettingsOpen(false);
    setExpandedInfoPanel(null);
    aiProcessedSignatureRef.current = '';
    pickOneAttemptedIdsRef.current.clear();
  }, [groupId]);

  useEffect(() => {
    if (!supportsMultipleMatches) {
      setMatchCount(1);
      return;
    }

    setMatchCount(current => Math.min(current, maxMatchCount));
  }, [maxMatchCount, supportsMultipleMatches]);

  const resetInputs = () => {
    setPriorityKeyword('');
    setPickedPlayer(null);
    setSecondPickedPlayer(null);
    setPickOnePlayers([]);
    setManualPlayers([null, null, null, null]);
  };

  const togglePriorityPlayer = player => {
    const playerId = Number(player.user_id);
    const lockedPartner = getLockedPartner(player);
    const partnerStatus = getStatus(lockedPartner);

    if (lockedPartner && partnerStatus !== 'ACTIVE') {
      setError(
        `คู่ล็อก ${lockedPartner.name} อยู่สถานะ ${partnerStatus || 'ไม่พร้อม'} ` +
        'จึงยังเลือกคู่นี้ไม่ได้'
      );
      return;
    }

    const selectionUnit = [player, lockedPartner]
      .filter(Boolean)
      .filter((item, index, items) =>
        items.findIndex(
          candidate =>
            Number(candidate.user_id) === Number(item.user_id)
        ) === index
      );
    const selectionIds = new Set(
      selectionUnit.map(item => Number(item.user_id))
    );
    const alreadySelected = selectedManualPlayers.some(
      item => Number(item.user_id) === playerId
    );
    const addingPlayers = selectionUnit.filter(
      item => !selectedManualPlayers.some(
        selected =>
          Number(selected.user_id) === Number(item.user_id)
      )
    );

    if (
      !alreadySelected &&
      selectedManualPlayers.length + addingPlayers.length > 4
    ) {
      setError(
        lockedPartner
          ? `ต้องเลือก ${player.name} พร้อมคู่ล็อก ${lockedPartner.name} ` +
          'แต่จำนวนผู้เล่นจะเกิน 4 คน'
          : 'เลือกผู้เล่นได้สูงสุด 4 คน กรุณาเอาคนเดิมออกก่อน'
      );
      return;
    }

    setError('');
    setManualPlayers(currentPlayers => {
      const currentSelected = currentPlayers.filter(Boolean);

      if (alreadySelected) {
        const remaining = currentSelected.filter(
          item => !selectionIds.has(Number(item.user_id))
        );
        return [...remaining, ...Array(4 - remaining.length).fill(null)];
      }

      const nextSelected = [...currentSelected, ...addingPlayers];
      return [...nextSelected, ...Array(4 - nextSelected.length).fill(null)];
    });
  };

  const handleApproveChange = enabled => {
    setApprovePlaying(enabled);
    setError('');

    if (!enabled) {
      if (pickedPlayer && getStatus(pickedPlayer) !== 'ACTIVE') {
        setPickedPlayer(null);
        setSecondPickedPlayer(null);
        setPickOnePlayers([]);
      }

      setManualPlayers(currentPlayers =>
        currentPlayers.map(player =>
          player && getStatus(player) !== 'ACTIVE' ? null : player
        )
      );
    }
  };

  const getLockedPartner = useCallback(player => {
    if (!player) return null;

    const playerId = Number(player.user_id);
    const lock = lockedPairs.find(item =>
      Number(item.user_a) === playerId ||
      Number(item.user_b) === playerId
    );

    if (!lock) return null;

    const partnerId =
      Number(lock.user_a) === playerId
        ? Number(lock.user_b)
        : Number(lock.user_a);

    return normalizedPlayers.find(
      item => Number(item.user_id) === partnerId
    ) || null;
  }, [lockedPairs, normalizedPlayers]);

  const pickedLockedPartner = getLockedPartner(pickedPlayer);
  const pickOneOrderedPlayers = useMemo(() => {
    const ordered = [
      pickedPlayer,
      secondPickedPlayer,
      pickedLockedPartner,
      ...pickOnePlayers
    ]
      .filter(Boolean);

    return ordered.filter((player, index, players) =>
      players.findIndex(
        item => Number(item.user_id) === Number(player.user_id)
      ) === index
    );
  }, [pickedLockedPartner, pickedPlayer, secondPickedPlayer, pickOnePlayers]);

  const pickOneNearbyCandidates = useMemo(() => {
    if (!pickedPlayer) return [];

    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(secondPickedPlayer?.user_id),
      Number(pickedLockedPartner?.user_id)
    ].filter(Number.isFinite));

    const keyword = nearbySearchKeyword.trim().toLowerCase();
    return normalizedPlayers
      .filter(player => !fixedIds.has(Number(player.user_id)))
      .filter(player =>
        selectedNearbyRanks.length === 0 ||
        selectedNearbyRanks.includes(
          normalizePlayerRank(player.level_rank, player.point)
        )
      )
      .filter(player =>
        !keyword || String(player.name || '').toLowerCase().includes(keyword)
      )
      .sort((left, right) => {
        const statusDiff =
          getPickOneStatusPriority(left) - getPickOneStatusPriority(right);
        if (statusDiff !== 0) return statusDiff;

        const gameDiff = getPlayerGames(left) - getPlayerGames(right);
        if (gameDiff !== 0) {
          return nearbyGameSort === 'DESC' ? -gameDiff : gameDiff;
        }

        return (
          getRankDistance(pickedPlayer, left) -
          getRankDistance(pickedPlayer, right) ||
          Math.abs(Number(left.point || 0) - Number(pickedPlayer.point || 0)) -
          Math.abs(Number(right.point || 0) - Number(pickedPlayer.point || 0)) ||
          comparePlayersByPriority(left, right, priority)
        );
      });
  }, [
    nearbyGameSort,
    nearbySearchKeyword,
    normalizedPlayers,
    pickedLockedPartner,
    selectedNearbyRanks,
    secondPickedPlayer,
    pickedPlayer,
    priority
  ]);

  const pickOneNearbyPlayers = pickOneNearbyCandidates;

  useEffect(() => {
    setNearbySearchKeyword('');
  }, [pickedPlayer?.user_id]);

  const toggleNearbyRank = rank => {
    setSelectedNearbyRanks(current => {
      if (current.includes(rank)) {
        return current.filter(item => item !== rank);
      }
      if (current.length >= 4) {
        setError('เลือก Rank ผู้เล่นใกล้เคียงได้สูงสุด 4 Rank');
        return current;
      }
      setError('');
      return [...current, rank];
    });
  };
  const togglePickOneNearbyPlayer = player => {
    if (!pickedPlayer) return;

    const playerId = Number(player.user_id);
    const candidatePartner = getLockedPartner(player);
    const anchorPartner = getLockedPartner(pickedPlayer);
    const fixedIds = new Set([
      Number(pickedPlayer.user_id),
      Number(secondPickedPlayer?.user_id),
      Number(anchorPartner?.user_id)
    ].filter(Number.isFinite));
    const selectionUnit = [player, candidatePartner]
      .filter(Boolean)
      .filter(item => !fixedIds.has(Number(item.user_id)))
      .filter((item, index, items) =>
        items.findIndex(
          candidate => Number(candidate.user_id) === Number(item.user_id)
        ) === index
      );
    const unitIds = new Set(
      selectionUnit.map(item => Number(item.user_id))
    );
    const alreadySelected = pickOnePlayers.some(
      item => Number(item.user_id) === playerId
    );

    if (alreadySelected) {
      setPickOnePlayers(current => current.filter(
        item => !unitIds.has(Number(item.user_id))
      ));
      setError('');
      return;
    }

    const unavailablePlayer = selectionUnit.find(
      item => getStatus(item) !== 'ACTIVE'
    );
    if (unavailablePlayer && !approvePlaying) {
      setError(
        `${unavailablePlayer.name} อยู่สถานะ ${getStatus(unavailablePlayer)} ` +
        'กรุณาเปิด Approve สถานะอื่นเพื่อจัด Match ล่วงหน้า'
      );
      return;
    }

    const currentIds = new Set(
      pickOnePlayers.map(item => Number(item.user_id))
    );
    const addingPlayers = selectionUnit.filter(
      item => !currentIds.has(Number(item.user_id))
    );
    const maximumAdditionalPlayers = 4 - fixedIds.size;

    if (pickOnePlayers.length + addingPlayers.length > maximumAdditionalPlayers) {
      setError(
        `Pick One เลือกเพิ่มได้อีกไม่เกิน ${maximumAdditionalPlayers} คน ` +
        '(รวม Locked Pair ต้องมีผู้เล่นไม่เกิน 4 คน)'
      );
      return;
    }

    setPickOnePlayers(current => [...current, ...addingPlayers]);
    setError('');
  };

  const selectedNonActivePlayers = () => {
    if (mode === 'PICK_ONE') {
      const lockedPartner = getLockedPartner(pickedPlayer);
      return [pickedPlayer, lockedPartner, ...pickOnePlayers]
        .filter(Boolean)
        .filter(player => getStatus(player) !== 'ACTIVE')
        .filter((player, index, players) =>
          players.findIndex(
            item => Number(item.user_id) === Number(player.user_id)
          ) === index
        );
    }

    if (usesManualSelection) {
      return manualPlayers.filter(
        player => player && getStatus(player) !== 'ACTIVE'
      );
    }

    return [];
  };

  const validate = () => {
    if (!groupId) return 'ไม่พบ Group';

    if (
      AUTOMATIC_MODES.has(mode) &&
      activePlayers.length < requiredPlayerCount
    ) {
      return (
        `มีผู้เล่น ACTIVE ${activePlayers.length} คน ` +
        `แต่การสร้าง ${matchCount} Match ต้องใช้ ${requiredPlayerCount} คน`
      );
    }

    if (mode === 'PICK_ONE' && !pickedPlayer) {
      return 'กรุณาเลือกผู้เล่นที่ต้องการล็อก';
    }

    if (mode === 'PICK_ONE') {
      const pickedId = Number(pickedPlayer.user_id);
      const lockedPartner = getLockedPartner(pickedPlayer);
      const pickedLock = lockedPairs.find(item =>
        Number(item.user_a) === pickedId ||
        Number(item.user_b) === pickedId
      );

      if (pickedLock && !lockedPartner) {
        return (
          `ไม่พบคู่ล็อกของ ${pickedPlayer.name} ในรายชื่อผู้เล่นที่ใช้งานได้`
        );
      }

      if (lockedPartner) {
        const partnerStatus = getStatus(lockedPartner);

        if (partnerStatus !== 'ACTIVE' && !approvePlaying) {
          return (
            `คู่ล็อก ${lockedPartner.name} อยู่สถานะ ${partnerStatus} ` +
            'กรุณาเปิด Approve สถานะอื่น'
          );
        }

      }

      const approvedPickIds = new Set(
        pickOneOrderedPlayers.map(player => Number(player.user_id))
      );
      const allowedPlayers = normalizedPlayers.filter(player => {
        const status = getStatus(player);
        return (
          status === 'ACTIVE' ||
          (
            approvePlaying &&
            approvedPickIds.has(Number(player.user_id))
          )
        );
      });

      if (allowedPlayers.length < 4) {
        return (
          `ผู้เล่นที่อยู่ในสถานะพร้อมมีเพียง ${allowedPlayers.length} คน ` +
          'ต้องมีอย่างน้อย 4 คน'
        );
      }

      // approvedPickIds มีผู้เล่นหลัก, Locked Pair และผู้เล่นที่เลือกเพิ่ม
      // ครบอยู่แล้ว ห้ามบวก pickOnePlayers ซ้ำอีกครั้ง
      if (approvedPickIds.size > 4) {
        return 'Pick One มีผู้เล่นที่เลือกและ Locked Pair รวมเกิน 4 คน';
      }
    }

    if (mode === 'PRIORITY_SELECT') {
      if (activePlayers.length < 4) {
        return (
          `มีผู้เล่น ACTIVE ${activePlayers.length} คน ` +
          'ต้องมีอย่างน้อย 4 คนจึงจะ Random ได้'
        );
      }

      if (selectedManualPlayers.length < 1) {
        return 'กรุณาเลือกผู้เล่นอย่างน้อย 1 คนก่อนกด Random';
      }
    }

    if (mode === 'MANUAL_4') {
      if (manualPlayers.some(player => !player)) {
        return 'กรุณาเลือกผู้เล่นให้ครบ 4 คน';
      }

      const ids = manualPlayers.map(player => Number(player.user_id));
      if (new Set(ids).size !== 4) return 'พบผู้เล่นซ้ำ';
    }

    const nonActiveSelections = selectedNonActivePlayers();
    if (nonActiveSelections.length > 0 && !approvePlaying) {
      return 'กรุณาเปิด Approve สถานะอื่นก่อนเลือกผู้เล่นที่ไม่ใช่ ACTIVE';
    }

    return '';
  };

  const loadPreviewHistory = async () => {
    const response = await fetch(
      `${API_BASE}/api/game-match/preview-history/${Number(groupId)}`
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || 'โหลดประวัติ 2 Match ล่าสุดไม่สำเร็จ');
    }
    const history = payload.history || {};
    setPreviewHistoryByUserId(history);
    return history;
  };

  const hasEncounterWithinTwoMatches = (players, history) => {
    const ids = players.map(player => Number(player.user_id));
    return ids.some((userId, index) => {
      const recentMatches = history?.[userId]?.recentMatches || [];
      const encounteredIds = new Set(
        recentMatches.flatMap(match => match.encounteredUserIds || [])
          .map(Number)
      );
      return ids.slice(index + 1).some(otherId => encounteredIds.has(otherId));
    });
  };

  const togglePreviewRank = rank => {
    setSelectedPreviewRanks(current => {
      if (current.includes(rank)) {
        setPreviewAlert(null);
        return current.filter(item => item !== rank);
      }
      if (current.length >= 4) {
        setError('เลือก Rank สำหรับ Preview ได้สูงสุด 4 Rank');
        setPreviewAlert({
          type: 'error',
          text: 'เลือก Rank ได้สูงสุด 4 Rank'
        });
        return current;
      }
      setError('');
      setPreviewAlert(null);
      return [...current, rank];
    });
  };

  const isPreviewRankAllowed = player =>
    selectedPreviewRanks.length === 0 ||
    selectedPreviewRanks.includes(
      normalizePlayerRank(player.level_rank, player.point)
    );

  const loadQueuePreviews = async () => {
    if (loading || previewLoading) return;

    const previewReadyPlayers = normalizedPlayers.filter(player =>
      ['ACTIVE', 'PLAY'].includes(getStatus(player)) &&
      !getQueueMatchId(player) &&
      isPreviewRankAllowed(player)
    );
    if (previewReadyPlayers.length < 4) {
      setError('ต้องมีผู้เล่น ACTIVE หรือ PLAY อย่างน้อย 4 คน');
      setPreviewAlert({
        type: 'error',
        text: `Rank ที่เลือกมีผู้เล่น ACTIVE/PLAY เพียง ${previewReadyPlayers.length} คน`
      });
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewManuallyEdited(false);
      setError('');
      setPreviewMatches([]);
      setPreviewAlert(null);
      setPreviewType('ACTIVE_RANK');
      // เปิด Modal ทันที เพื่อให้เห็นสถานะ Loading และไม่ดูเหมือนปุ่มไม่ทำงาน
      setPreviewOpen(true);
      previewRealtimeHandledRef.current = previewRealtimeVersion;
      const previewHistory = await loadPreviewHistory();

      // ขอหลาย seed แล้วตัด Match ซ้ำ เพื่อแสดงตัวเลือกได้สูงสุด 5 Match
      const requests = Array.from({ length: 20 }, (_, index) =>
        postMatchmakingJson('/api/game-match/random', {
          groupId: Number(groupId),
          mode: 'AI_RANDOM',
          priority: 'FIFO',
          playStyle: 'ANY',
          matchCount: 1,
          seed: `preview-${Date.now()}-${index}`,
          allowRangeOverride: false,
          allowRepeatPartnerOverride: false,
          allowRecentEncounterOverride: false
        }).catch(() => null)
      );
      const results = await Promise.all(requests);
      const uniqueMatches = [];
      const signatures = new Set();

      results.filter(Boolean).forEach(result => {
        const players = [...(result.team1 || []), ...(result.team2 || [])];
        if (players.length !== 4) return;
        if (players.some(player =>
          !['ACTIVE', 'PLAY'].includes(getStatus(player))
        )) return;
        if (players.some(player => !isPreviewRankAllowed(player))) return;
        if (hasEncounterWithinTwoMatches(players, previewHistory)) return;

        const rankIndexes = players.map(player =>
          RANK_ORDER.indexOf(normalizePlayerRank(player.level_rank, player.point))
        );
        const rankSpread = Math.max(...rankIndexes) - Math.min(...rankIndexes);
        // Rank ใกล้กันหนึ่งระดับยังถือเป็นกลุ่มระดับเดียวกันสำหรับการจัดทีม
        if (selectedPreviewRanks.length === 0 && rankSpread > 1) return;

        const signature = players
          .map(player => Number(player.user_id))
          .sort((a, b) => a - b)
          .join('-');
        if (signatures.has(signature)) return;

        signatures.add(signature);
        const rankLabels = [...new Set(players.map(player =>
          normalizePlayerRank(player.level_rank, player.point)
        ))];
        uniqueMatches.push({
          ...result,
          previewRank: rankLabels.join(' / '),
          rankSpread
        });
      });

      // Backend อาจคืนชุดเดิมทุก seed หรือไม่มีชุด Rank เดียวกันเป๊ะ
      // สร้างตัวเลือกสำรองจาก ACTIVE โดยเลือก Rank ใกล้ที่สุดและทีมคะแนนสูสี
      if (uniqueMatches.length < 5) {
        const pool = [...previewReadyPlayers]
          .sort((left, right) => {
            const rankDiff = RANK_ORDER.indexOf(
              normalizePlayerRank(left.level_rank, left.point)
            ) - RANK_ORDER.indexOf(
              normalizePlayerRank(right.level_rank, right.point)
            );
            return rankDiff || Number(left.point || 0) - Number(right.point || 0);
          })
          .slice(0, 24);
        const localCandidates = [];

        for (let a = 0; a < pool.length - 3; a += 1) {
          for (let b = a + 1; b < pool.length - 2; b += 1) {
            for (let c = b + 1; c < pool.length - 1; c += 1) {
              for (let d = c + 1; d < pool.length; d += 1) {
                const group = [pool[a], pool[b], pool[c], pool[d]];
                if (hasEncounterWithinTwoMatches(group, previewHistory)) continue;
                const selectedIds = new Set(
                  group.map(player => Number(player.user_id))
                );
                const respectsLockedSelection = lockedPairs.every(lock =>
                  selectedIds.has(Number(lock.user_a)) ===
                  selectedIds.has(Number(lock.user_b))
                );
                if (!respectsLockedSelection) continue;
                const rankIndexes = group.map(player => RANK_ORDER.indexOf(
                  normalizePlayerRank(player.level_rank, player.point)
                ));
                const rankSpread = Math.max(...rankIndexes) - Math.min(...rankIndexes);
                const layouts = [
                  [[group[0], group[1]], [group[2], group[3]]],
                  [[group[0], group[2]], [group[1], group[3]]],
                  [[group[0], group[3]], [group[1], group[2]]]
                ];
                const validLayouts = layouts.filter(([team1, team2]) => {
                  const team1Ids = new Set(team1.map(player => Number(player.user_id)));
                  const team2Ids = new Set(team2.map(player => Number(player.user_id)));
                  return lockedPairs.every(lock => {
                    const userA = Number(lock.user_a);
                    const userB = Number(lock.user_b);
                    if (!selectedIds.has(userA)) return true;
                    return (
                      (team1Ids.has(userA) && team1Ids.has(userB)) ||
                      (team2Ids.has(userA) && team2Ids.has(userB))
                    );
                  });
                });
                if (validLayouts.length === 0) continue;
                const scoredLayouts = validLayouts
                  .map(([team1, team2]) => ({
                    team1,
                    team2,
                    teamGap: Math.abs(
                      team1.reduce((sum, player) => sum + Number(player.point || 0), 0) -
                      team2.reduce((sum, player) => sum + Number(player.point || 0), 0)
                    )
                  }))
                  .sort((left, right) => left.teamGap - right.teamGap);
                const bestLayout = scoredLayouts[0];

                localCandidates.push({
                  ...bestLayout,
                  rankSpread,
                  previewRank: [...new Set(group.map(player =>
                    normalizePlayerRank(player.level_rank, player.point)
                  ))].join(' / '),
                  allowedLayouts: scoredLayouts,
                  hasLockedPair: lockedPairs.some(lock =>
                    selectedIds.has(Number(lock.user_a))
                  ),
                  analysis: { teamGap: bestLayout.teamGap },
                  reason: 'เลือกผู้เล่น ACTIVE Rank ใกล้กันที่สุดและจัดทีมตามคะแนน'
                });
              }
            }
          }
        }

        localCandidates
          .sort((left, right) =>
            Number(right.hasLockedPair) - Number(left.hasLockedPair) ||
            left.teamGap - right.teamGap ||
            left.rankSpread - right.rankSpread
          )
          .forEach(candidate => {
            if (uniqueMatches.length >= 5) return;
            const signature = [...candidate.team1, ...candidate.team2]
              .map(player => Number(player.user_id))
              .sort((left, right) => left - right)
              .join('-');
            if (signatures.has(signature)) return;
            signatures.add(signature);
            uniqueMatches.push(candidate);
          });
      }

      uniqueMatches.sort((left, right) =>
        Number(right.hasLockedPair) - Number(left.hasLockedPair) ||
        Number(left.analysis?.teamGap || left.teamGap || 0) -
          Number(right.analysis?.teamGap || right.teamGap || 0) ||
        Number(left.rankSpread || 0) - Number(right.rankSpread || 0)
      );
      setPreviewMatches(uniqueMatches.slice(0, 5));
      if (uniqueMatches.length === 0) {
        setError('ยังจัด Preview ไม่ได้ กรุณาตรวจสอบผู้เล่น ACTIVE');
      }
    } catch (previewError) {
      setError(previewError.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadFixedPlayerPreviews = async () => {
    if (loading || previewLoading) return;
    if (!pickedPlayer) {
      setError('กรุณาเลือกผู้เล่นหลักในโหมด Pick One ก่อน');
      return;
    }

    const anchorPlayers = [pickedPlayer, secondPickedPlayer].filter(Boolean);
    const queuedAnchor = anchorPlayers.find(player => getQueueMatchId(player));
    if (queuedAnchor) {
      setError(`${queuedAnchor.name} อยู่ใน Match Queue แล้ว กรุณาเลือกผู้เล่นหลักคนอื่น`);
      setPreviewAlert({
        type: 'error',
        text: `${queuedAnchor.name} อยู่ใน Match Queue แล้ว จึงไม่สามารถใช้สร้าง Preview ใหม่ได้`
      });
      return;
    }
    const anchorIds = new Set(anchorPlayers.map(player => Number(player.user_id)));

    // Locked Pair ของผู้เล่นหลักต้องเข้ามาใน Match และอยู่ทีมเดียวกันเสมอ
    for (const lock of lockedPairs) {
      const userA = Number(lock.user_a);
      const userB = Number(lock.user_b);
      const hasA = anchorIds.has(userA);
      const hasB = anchorIds.has(userB);
      if (hasA === hasB) continue;

      const partnerId = hasA ? userB : userA;
      const partner = normalizedPlayers.find(
        player => Number(player.user_id) === partnerId
      );
      if (!partner || getStatus(partner) === 'HOLD') {
        setError('Locked Pair ของผู้เล่นหลักไม่พร้อมลง Match');
        return;
      }
      anchorPlayers.push(partner);
      anchorIds.add(partnerId);
    }

    if (anchorPlayers.length > 2) {
      setError('ผู้เล่นหลัก 2 คนมี Locked Pair เพิ่ม ทำให้ทีมมีผู้เล่นเกิน 2 คน');
      return;
    }

    const candidates = normalizedPlayers
      .filter(player =>
        !anchorIds.has(Number(player.user_id)) &&
        getStatus(player) !== 'HOLD' &&
        !getQueueMatchId(player) &&
        isPreviewRankAllowed(player)
      )
      .sort((left, right) => {
        const statusDiff =
          Number(getStatus(right) === 'ACTIVE') -
          Number(getStatus(left) === 'ACTIVE');
        const rankDiff = getRankDistance(pickedPlayer, left) -
          getRankDistance(pickedPlayer, right);
        const pointDiff = Math.abs(Number(pickedPlayer.point) - Number(left.point)) -
          Math.abs(Number(pickedPlayer.point) - Number(right.point));
        return statusDiff || rankDiff || pointDiff;
      })
      .slice(0, 18);

    const requiredCandidateCount = 4 - anchorPlayers.length;
    if (candidates.length < requiredCandidateCount) {
      setError(`ต้องมีผู้เล่นที่ไม่ใช่ HOLD เพิ่มอีก ${requiredCandidateCount} คน`);
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewManuallyEdited(false);
      setError('');
      setPreviewType('FIXED_PLAYER');
      setPreviewMatches([]);
      setPreviewAlert(null);
      setPreviewOpen(true);
      previewRealtimeHandledRef.current = previewRealtimeVersion;
      const previewHistory = await loadPreviewHistory();

      const choose = (items, count, start = 0, selected = [], result = []) => {
        if (selected.length === count) {
          result.push([...selected]);
          return result;
        }
        for (let index = start; index < items.length; index += 1) {
          selected.push(items[index]);
          choose(items, count, index + 1, selected, result);
          selected.pop();
        }
        return result;
      };
      const options = [];
      const groups = choose(candidates, requiredCandidateCount);

      groups.forEach(selectedCandidates => {
            const group = [...anchorPlayers, ...selectedCandidates];
            const requiresRecentEncounterApproval =
              hasEncounterWithinTwoMatches(group, previewHistory);
            const selectedIds = new Set(group.map(player => Number(player.user_id)));
            const respectsLockedSelection = lockedPairs.every(lock => {
              const hasA = selectedIds.has(Number(lock.user_a));
              const hasB = selectedIds.has(Number(lock.user_b));
              return hasA === hasB;
            });
            if (!respectsLockedSelection) return;

            const layouts = [
              [[group[0], group[1]], [group[2], group[3]]],
              [[group[0], group[2]], [group[1], group[3]]],
              [[group[0], group[3]], [group[1], group[2]]]
            ];
            const validLayouts = layouts.filter(([team1, team2]) => {
              const team1Ids = new Set(team1.map(player => Number(player.user_id)));
              const team2Ids = new Set(team2.map(player => Number(player.user_id)));
              const anchorsTogether = anchorPlayers.every(player =>
                team1Ids.has(Number(player.user_id))
              ) || anchorPlayers.every(player =>
                team2Ids.has(Number(player.user_id))
              );
              const locksTogether = lockedPairs.every(lock => {
                const userA = Number(lock.user_a);
                const userB = Number(lock.user_b);
                if (!selectedIds.has(userA)) return true;
                return (
                  (team1Ids.has(userA) && team1Ids.has(userB)) ||
                  (team2Ids.has(userA) && team2Ids.has(userB))
                );
              });
              return anchorsTogether && locksTogether;
            });
            if (validLayouts.length === 0) return;

            const scoredLayouts = validLayouts
              .map(([team1, team2]) => ({
                team1,
                team2,
                teamGap: Math.abs(
                  team1.reduce((sum, player) => sum + Number(player.point || 0), 0) -
                  team2.reduce((sum, player) => sum + Number(player.point || 0), 0)
                )
              }))
              .sort((left, right) => left.teamGap - right.teamGap);
            const bestLayout = scoredLayouts[0];
            const rankSpread = Math.max(...group.map(player =>
              RANK_ORDER.indexOf(normalizePlayerRank(player.level_rank, player.point))
            )) - Math.min(...group.map(player =>
              RANK_ORDER.indexOf(normalizePlayerRank(player.level_rank, player.point))
            ));

            options.push({
              ...bestLayout,
              rankSpread,
              previewRank: [...new Set(group.map(player =>
                normalizePlayerRank(player.level_rank, player.point)
              ))].join(' / '),
              allowedLayouts: scoredLayouts,
              hasLockedPair: lockedPairs.some(lock =>
                selectedIds.has(Number(lock.user_a))
              ),
              analysis: { teamGap: bestLayout.teamGap },
              requiresRecentEncounterApproval,
              reason: `ยึด ${anchorPlayers.map(player => player.name).join(' + ')} อยู่ทีมเดียวกัน`
            });
      });

      options.sort((left, right) =>
        Number(left.requiresRecentEncounterApproval) -
          Number(right.requiresRecentEncounterApproval) ||
        Number(right.hasLockedPair) - Number(left.hasLockedPair) ||
        left.teamGap - right.teamGap || left.rankSpread - right.rankSpread
      );
      setPreviewMatches(options.slice(0, 3));
    } finally {
      setPreviewLoading(false);
    }
  };

  const refreshCurrentPreview = () => {
    setPreviewManuallyEdited(false);
    setPreviewAlert(null);
    if (previewType === 'FIXED_PLAYER') {
      loadFixedPlayerPreviews();
      return;
    }
    loadQueuePreviews();
  };

  const refreshCurrentPreviewRef = useRef(refreshCurrentPreview);
  refreshCurrentPreviewRef.current = refreshCurrentPreview;

  useEffect(() => {
    if (!previewOpen) {
      previewRealtimeHandledRef.current = previewRealtimeVersion;
      return undefined;
    }
    if (previewRealtimeHandledRef.current === previewRealtimeVersion) {
      return undefined;
    }

    previewRealtimeHandledRef.current = previewRealtimeVersion;
    const timerId = window.setTimeout(() => {
      if (
        !previewManuallyEdited &&
        !previewLoading &&
        previewCreatingIndex === null
      ) {
        refreshCurrentPreviewRef.current();
        setPreviewAlert({
          type: 'info',
          text: 'อัปเดต Preview แบบ Realtime ตามสถานะล่าสุดแล้ว'
        });
      }
    }, 300);

    return () => window.clearTimeout(timerId);
  }, [
    previewRealtimeVersion,
    previewOpen,
    previewManuallyEdited,
    previewLoading,
    previewCreatingIndex
  ]);

  const swapPreviewPair = matchIndex => {
    setPreviewMatches(current => current.map((match, index) => {
      if (index !== matchIndex) return match;

      const [a, b] = match.team1;
      const [c, d] = match.team2;
      const swapStep = Number(match.swapStep || 0);
      const defaultLayouts = [
        [[a, b], [c, d]],
        [[a, c], [b, d]],
        [[a, d], [b, c]]
      ];
      const teamLayouts = Array.isArray(match.allowedLayouts) &&
        match.allowedLayouts.length > 0
        ? match.allowedLayouts.map(layout => [layout.team1, layout.team2])
        : defaultLayouts;
      const nextStep = (swapStep + 1) % teamLayouts.length;

      return {
        ...match,
        team1: teamLayouts[nextStep][0],
        team2: teamLayouts[nextStep][1],
        swapStep: nextStep,
        analysis: {
          ...match.analysis,
          teamGap: Math.abs(
            teamLayouts[nextStep][0].reduce(
              (sum, player) => sum + Number(player.point || 0), 0
            ) - teamLayouts[nextStep][1].reduce(
              (sum, player) => sum + Number(player.point || 0), 0
            )
          )
        }
      };
    }));
  };

  const editPreviewPlayer = (matchIndex, teamKey, playerIndex, userId) => {
    const replacement = normalizedPlayers.find(
      player => Number(player.user_id) === Number(userId)
    );
    if (!replacement) return;
    setPreviewManuallyEdited(true);

    setPreviewMatches(current => current.map((match, index) => {
      if (index !== matchIndex) return match;

      const existingIds = [...(match.team1 || []), ...(match.team2 || [])]
        .map(player => Number(player.user_id));
      const replacedPlayer = match?.[teamKey]?.[playerIndex];
      if (
        existingIds.includes(Number(replacement.user_id)) &&
        Number(replacedPlayer?.user_id) !== Number(replacement.user_id)
      ) {
        setPreviewAlert({ type: 'error', text: 'ผู้เล่นคนนี้อยู่ใน Match แล้ว' });
        return match;
      }

      const nextTeam = [...(match[teamKey] || [])];
      nextTeam[playerIndex] = replacement;
      const nextTeam1 = teamKey === 'team1' ? nextTeam : match.team1;
      const nextTeam2 = teamKey === 'team2' ? nextTeam : match.team2;
      const teamGap = Math.abs(
        nextTeam1.reduce((sum, player) => sum + Number(player.point || 0), 0) -
        nextTeam2.reduce((sum, player) => sum + Number(player.point || 0), 0)
      );

      setPreviewAlert({
        type: 'info',
        text: `เปลี่ยนเฉพาะผู้เล่นเป็น ${replacement.name} แล้ว • คงคู่และ Match เดิม • Team gap ${teamGap}`
      });
      setEditingPreviewSlot(null);
      return {
        ...match,
        [teamKey]: nextTeam,
        allowedLayouts: null,
        previewRank: [...new Set([...nextTeam1, ...nextTeam2].map(player =>
          normalizePlayerRank(player.level_rank, player.point)
        ))].join(' / '),
        analysis: { ...match.analysis, teamGap }
      };
    }));
  };

  const createQueueFromPreview = async matchIndex => {
    if (previewCreatingIndex !== null) return;
    const match = previewMatches[matchIndex];
    if (!match) return;

    const payload = {
      groupId: Number(groupId),
      mode: 'PICK_ONE',
      team1: match.team1,
      team2: match.team2,
      approveAllStatuses: true,
      approveRecentEncounter: false
    };

    try {
      setPreviewCreatingIndex(matchIndex);
      setError('');
      let response;

      try {
        response = await postMatchmakingJson('/api/game-match/queue', payload);
      } catch (queueError) {
        if (!queueError.data?.requiresRecentEncounterApproval) throw queueError;

        const approved = window.confirm(
          'Match นี้ผิดกติกาเกมล่าสุด ต้องการ Approve และสร้าง Queue หรือไม่?'
        );
        if (!approved) return;

        response = await postMatchmakingJson('/api/game-match/queue', {
          ...payload,
          approveRecentEncounter: true
        });
      }

      setLastResult({
        ...response,
        matches: [{ ...match, ...response }]
      });
      const queuedIds = new Set(
        [...match.team1, ...match.team2].map(player => Number(player.user_id))
      );
      setPreviewMatches(current => current.filter(candidate =>
        [...candidate.team1, ...candidate.team2].every(
          player => !queuedIds.has(Number(player.user_id))
        )
      ));
      setPreviewAlert({
        type: 'success',
        text:
          `สร้าง Queue สำเร็จ และอัปเดตตัวเลือกอื่นที่มี ` +
          `${[...match.team1, ...match.team2].map(player => player.name).join(', ')} แล้ว`
      });

      if (onCreatedRef.current) await onCreatedRef.current(response);
    } catch (queueError) {
      setError(queueError.message);
      setPreviewAlert({ type: 'error', text: queueError.message });
    } finally {
      setPreviewCreatingIndex(null);
    }
  };

  const createQueueFromSelectedPlayers = async () => {
    if (loading || pickOneOrderedPlayers.length !== 4) return;

    const team1 = pickOneOrderedPlayers.slice(0, 2);
    const team2 = pickOneOrderedPlayers.slice(2, 4);
    const payload = {
      groupId: Number(groupId),
      mode: 'PICK_ONE',
      team1,
      team2,
      approveAllStatuses: true,
      approveRecentEncounter: false
    };

    try {
      setLoading(true);
      setError('');
      let response;

      try {
        response = await postMatchmakingJson('/api/game-match/queue', payload);
      } catch (queueError) {
        if (!queueError.data?.requiresRecentEncounterApproval) throw queueError;
        const approved = window.confirm(
          'ผู้เล่นที่เลือกติดกติกา Match ล่าสุด ต้องการ Approve และสร้าง Queue หรือไม่?'
        );
        if (!approved) return;
        response = await postMatchmakingJson('/api/game-match/queue', {
          ...payload,
          approveRecentEncounter: true
        });
      }

      setLastResult({
        ...response,
        matches: [{ ...response, team1, team2, mode: 'PICK_ONE' }]
      });
      setPickedPlayer(null);
      setSecondPickedPlayer(null);
      setPickOnePlayers([]);
      setNearbySearchKeyword('');
      if (onCreatedRef.current) await onCreatedRef.current(response);
    } catch (queueError) {
      setError(queueError.message);
    } finally {
      setLoading(false);
    }
  };

  const generateQueue = async () => {
    if (loading || aiAutoLoading) return;

    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const body = {
        groupId: Number(groupId),
        mode,
        priority: mode === 'AI_RANDOM' ? 'FIFO' : priority,
        pointRange,
        playStyle: mode === 'AI_RANDOM' ? 'ANY' : playStyle,
        matchCount: supportsMultipleMatches ? matchCount : 1,
        approvePlaying: canApprovePlaying && approvePlaying,
        fixedUserId:
          mode === 'PICK_ONE' ? Number(pickedPlayer.user_id) : null,
        selectedPlayerIds:
          mode === 'PICK_ONE'
            ? pickOnePlayers.map(player => Number(player.user_id))
            : usesManualSelection
              ? (
                mode === 'PRIORITY_SELECT'
                  ? orderedPrioritySelections
                  : selectedManualPlayers
              ).map(
                player => Number(player.user_id)
              )
              : []
      };

      const requestQueue = async payload => {
        const response = await fetch(
          `${API_BASE}/api/game-match/generate-queue`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );
        const responseData = await response.json().catch(() => ({}));

        return { response, responseData };
      };

      let requestBody = {
        ...body,
        allowRangeOverride: false,
        allowRepeatPartnerOverride: false,
        allowRecentEncounterOverride: false
      };
      let res;
      let data;

      // อาจต้องยืนยันมากกว่า 1 ชั้น เช่น คะแนนเกินช่วง แล้วจึงพบคู่ทีมซ้ำ
      for (let confirmationRound = 0; confirmationRound < 3; confirmationRound += 1) {
        ({ response: res, responseData: data } = await requestQueue(
          requestBody
        ));

        if (res.ok) break;

        if (
          data.confirmationType === 'RANGE' &&
          usesIndividualPointRange &&
          !requestBody.allowRangeOverride
        ) {
          const gaps = Array.isArray(data.individualGaps)
            ? data.individualGaps.join(' / ')
            : '-';
          const teammateGaps = Array.isArray(data.teammateGaps)
            ? data.teammateGaps.join(' / ')
            : '-';
          const requiredRange = data.requiredRange ?? gaps;
          const confirmed = window.confirm(
            `⚠️ ไม่พบทีมที่ผ่านช่วง ${pointRange} คะแนน\n` +
            `คะแนนห่างข้ามทีม: ${gaps}\n` +
            `คะแนนห่างภายในทีม T1 / T2: ${teammateGaps}\n` +
            `ต้องใช้ช่วงสูงสุด ${requiredRange} คะแนน\n` +
            'ระบบยังคงรักษา Locked Pair ให้อยู่ทีมเดียวกัน\n\n' +
            'ต้องการ Approve คะแนนห่างและสร้าง Match ด้วยคู่นี้หรือไม่?'
          );

          if (!confirmed) {
            setError('ยกเลิกการสร้าง Match เพราะคะแนนเกินช่วงที่กำหนด');
            return;
          }

          requestBody = {
            ...requestBody,
            allowRangeOverride: true
          };
          continue;
        }

        if (
          (
            data.confirmationType === 'RECENT_ENCOUNTER' ||
            data.confirmationType === 'REPEAT_PARTNER'
          ) &&
          !requestBody.allowRecentEncounterOverride
        ) {
          const repeatPairs = Array.isArray(data.recentEncounterPairs)
            ? data.recentEncounterPairs
            : Array.isArray(data.repeatPartnerPairs)
              ? data.repeatPartnerPairs
              : [];
          const pairNames = repeatPairs.length > 0
            ? repeatPairs
              .map(pair => {
                const relation = pair.relation === 'TEAMMATE'
                  ? 'ทีมเดียวกัน'
                  : pair.relation === 'OPPONENT'
                    ? 'คนละฝั่ง'
                    : 'อยู่ในแมทช์เดียวกัน';
                return `${pair.nameA} ↔ ${pair.nameB} (${relation})`;
              })
              .join('\n')
            : 'ผู้เล่นที่เพิ่งพบกัน';
          const expansionNote =
            mode === 'PICK_ONE' || mode === 'AI_RANDOM'
              ? 'ระบบลองขยับช่วงคะแนนและ Rank เพื่อหาชุดที่ใกล้ที่สุดแล้ว แต่'
              : '';
          const confirmed = window.confirm(
            `⚠️ ไม่มีชุดผู้เล่นที่ผ่านกติกา 2 เกมล่าสุด\n${pairNames}\n\n` +
            `${expansionNote}รายชื่อข้างต้นยังเคยอยู่ใน Match เดียวกันภายใน 2 เกมล่าสุด ` +
            'ไม่ว่าจะเป็นทีมเดียวกันหรือคนละฝั่ง\n\n' +
            'ต้องการ Approve และสร้าง Match นี้หรือไม่?'
          );

          if (!confirmed) {
            setError('ยังไม่ได้สร้าง Match เพราะยังไม่ได้ Approve ผู้เล่นซ้ำใน 2 เกมล่าสุด');
            return;
          }

          requestBody = {
            ...requestBody,
            allowRepeatPartnerOverride: true,
            allowRecentEncounterOverride: true
          };
          continue;
        }

        break;
      }

      if (!res.ok) {
        throw new Error(
          data.message ||
          data.error ||
          `สร้าง Queue ไม่สำเร็จ (HTTP ${res.status})`
        );
      }

      setLastResult(data);


      /* =================================================
         โหลด Queue + Players ใหม่ก่อน
      ================================================= */

      if (onCreatedRef.current) {
        await onCreatedRef.current(data);
      }


      /* =================================================
         ค่อยเคลียร์ selection หลังข้อมูลใหม่มาแล้ว
      ================================================= */

      if (mode === 'PICK_ONE') {

        setPickedPlayer(null);

        setPickOnePlayers([]);

        setNearbySearchKeyword('');

        setAiRecommendation(null);

        aiProcessedSignatureRef.current = '';

        pickOneAttemptedIdsRef.current.clear();

      } else {

        resetInputs();

      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };
  void generateQueue;

  useEffect(() => {
    const supportsAiAuto =
      mode === 'AI_RANDOM' || mode === 'PICK_ONE';

    if (!supportsAiAuto) {
      setAiRecommendation(null);
      setAiAutomationStatus('');
      setAiAutoLoading(false);
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    if (mode === 'PICK_ONE' && !pickedPlayer) {
      setAiRecommendation(null);
      setAiAutomationStatus('เลือกผู้เล่นหลักก่อน เพื่อให้ AI ค้นหาคะแนนใกล้เคียง');
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    const approvedAdvancePlayerCount =
      mode === 'PICK_ONE' && approvePlaying
        ? pickOneOrderedPlayers.filter(
          player => getStatus(player) !== 'ACTIVE'
        ).length
        : 0;
    const availablePlayerCount =
      aiOrderedActivePlayers.length + approvedAdvancePlayerCount;
    const missingPlayerCount = Math.max(
      0,
      requiredPlayerCount - availablePlayerCount
    );

    if (!groupId || missingPlayerCount > 0) {
      setAiAutomationStatus(
        `กำลังรอผู้เล่น ACTIVE อีก ${missingPlayerCount} คน`
      );
      aiProcessedSignatureRef.current = '';
      return undefined;
    }

    if (
      aiRequestInFlightRef.current ||
      aiProcessedSignatureRef.current === aiMatchmakingSignature
    ) {
      return undefined;
    }

    let cancelled = false;
    const timerId = window.setTimeout(async () => {
      aiRequestInFlightRef.current = true;
      setAiAutoLoading(true);
      setError('');
      setAiAutomationStatus(
        mode === 'PICK_ONE'
          ? `AI กำลังค้นหาผู้เล่นคะแนนใกล้เคียง ${pickedPlayer.name}...`
          : 'AI กำลังวิเคราะห์ผู้เล่น ACTIVE ทั้งก๊วน...'
      );

      const body = {
        groupId: Number(groupId),
        mode,
        priority: mode === 'AI_RANDOM' ? 'FIFO' : priority,
        pointRange,
        playStyle: mode === 'AI_RANDOM' ? 'ANY' : playStyle,
        matchCount: mode === 'AI_RANDOM' ? matchCount : 1,
        approvePlaying: mode === 'PICK_ONE' && approvePlaying,
        fixedUserId:
          mode === 'PICK_ONE' ? Number(pickedPlayer.user_id) : null,
        selectedPlayerIds:
          mode === 'PICK_ONE'
            ? pickOnePlayers.map(player => Number(player.user_id))
            : [],
        // Pick One Auto ต้องจัดลงให้ได้ โดยยังเลือกชุดที่คะแนนใกล้ที่สุด
        // จากผู้เล่นที่มีอยู่ หากไม่มีชุดที่อยู่ในช่วงคะแนนที่ตั้งไว้
        allowRangeOverride: mode === 'PICK_ONE',
        allowRepeatPartnerOverride: false,
        allowRecentEncounterOverride: false
      };

      try {
        const preview = await postMatchmakingJson(
          '/api/game-match/random',
          body
        );

        if (cancelled) return;

        setAiRecommendation(preview);
        aiProcessedSignatureRef.current = aiMatchmakingSignature;
        setAiAutomationStatus(
          aiAutoEnabled
            ? mode === 'PICK_ONE'
              ? 'พบผู้เล่นคะแนนใกล้เคียงแล้ว กำลังสร้าง Queue อัตโนมัติ...'
              : `พบ Match แนะนำแล้ว กำลังสร้าง Queue ${matchCount} Match อัตโนมัติ...`
            : 'อัปเดต Match แนะนำแบบเรียลไทม์แล้ว'
        );

        if (!aiAutoEnabled) return;

        await new Promise(resolve => {
          window.setTimeout(resolve, 700);
        });

        if (cancelled) return;

        const created = await postMatchmakingJson(
          '/api/game-match/generate-queue',
          body
        );

        if (cancelled) return;

        const createdRecommendation =
          Array.isArray(created.matches) && created.matches.length > 0
            ? created.matches[0]
            : created;

        setAiRecommendation(
          createdRecommendation
        );

        setLastResult(created);

        setAiAutomationStatus(
          `สร้าง Queue ${created.matchCount ||
          (mode === 'PICK_ONE' ? 1 : matchCount)
          } Match อัตโนมัติสำเร็จ`
        );


        /* =================================================
           REFRESH SERVER DATA ก่อน
        ================================================= */

        if (onCreatedRef.current) {

          await onCreatedRef.current(
            created
          );

        }


        /* =================================================
           PICK ONE -> ADVANCE
        ================================================= */

        if (mode === 'PICK_ONE') {

          setPickedPlayer(null);

          setPickOnePlayers([]);

          setNearbySearchKeyword('');

          setAiRecommendation(null);

          aiProcessedSignatureRef.current = '';

          pickOneAttemptedIdsRef.current.clear();

        }
      } catch (requestError) {
        if (!cancelled) {
          if (mode === 'PICK_ONE' && aiAutoEnabled) {
            const currentId = Number(pickedPlayer?.user_id);
            pickOneAttemptedIdsRef.current.add(currentId);

            const nextPlayer = orderedSelectablePlayers.find(player => {
              const playerId = Number(player.user_id);
              return (
                playerId !== currentId &&
                !pickOneAttemptedIdsRef.current.has(playerId)
              );
            });

            if (nextPlayer) {
              setPickedPlayer(nextPlayer);
              setPickOnePlayers([]);
              setAiRecommendation(null);
              setError('');
              setAiAutomationStatus(
                `${pickedPlayer.name} จัดไม่ได้ • กำลังลองผู้เล่นถัดไป ${nextPlayer.name}`
              );
              aiProcessedSignatureRef.current = '';
              return;
            }
          }

          aiProcessedSignatureRef.current = aiMatchmakingSignature;
          setError(requestError.message);
          if (
            requestError.data?.requiresRecentEncounterApproval ||
            requestError.data?.requiresRepeatPartnerApproval
          ) {
            setAiAutoEnabled(false);
          }
          setAiAutomationStatus(
            requestError.data?.requiresRecentEncounterApproval ||
              requestError.data?.requiresRepeatPartnerApproval
              ? 'พบผู้เล่นที่อยู่ใน 2 เกมล่าสุด กรุณากดสร้าง Queue เพื่อ Approve'
              : 'AI ยังสร้าง Queue ไม่สำเร็จ'
          );
        }
      } finally {
        aiRequestInFlightRef.current = false;
        setAiAutoLoading(false);
        setAiAutomationCycle(current => current + 1);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [
    aiAutoEnabled,
    aiAutomationCycle,
    aiMatchmakingSignature,
    aiOrderedActivePlayers.length,
    approvePlaying,
    groupId,
    matchCount,
    mode,
    orderedSelectablePlayers,
    pickOnePlayers,
    pickOneOrderedPlayers,
    pickedPlayer,
    playStyle,
    pointRange,
    priority,
    requiredPlayerCount
  ]);

  const renderTeam = (title, players) => (
    <div className="rmp-result-team">
      <strong>{title}</strong>
      {players.map(player => (
        <div key={player.user_id} className="rmp-result-player">
          <span>
            {player.name}
            <small
              className={`rmp-inline-status status-${getStatus(player).toLowerCase()}`}
              style={{ marginLeft: 6 }}
            >
              {getStatus(player)}
            </small>
          </span>
          <span>
            {normalizePlayerRank(player.level_rank, player.point)} • {player.point}
          </span>
        </div>
      ))}
    </div>
  );

  const renderPreviewTeam = (title, players, matchIndex, teamKey) => {
    const playerNameById = new Map(
      normalizedPlayers.map(player => [Number(player.user_id), player.name])
    );

    return (
      <div className="rmp-result-team">
        <strong>{title}</strong>
        {players.map((player, playerIndex) => {
          const latestMatch =
            previewHistoryByUserId?.[Number(player.user_id)]?.recentMatches?.[0];
          const teammateNames = (latestMatch?.teammates || [])
            .map(id => playerNameById.get(Number(id)) || `#${id}`);
          const opponentNames = (latestMatch?.opponents || [])
            .map(id => playerNameById.get(Number(id)) || `#${id}`);

          return (
            <div
              key={player.user_id}
              className="rmp-result-player"
              style={{ display: 'grid', gridTemplateColumns: '42px 1fr auto', gap: 8 }}
            >
              <PlayerAvatar player={player} size="normal" />
              <span>
                <b>{player.name}</b>{' '}
                <small className={`rmp-inline-status status-${getStatus(player).toLowerCase()}`}>
                  {getStatus(player)}
                </small>
                {['QUEUE', 'PLAY'].includes(getStatus(player)) && (
                  <small style={{ display: 'block', color: '#1d4ed8', fontWeight: 700 }}>
                    {getPlayerMatchStatusLabel(player)}
                  </small>
                )}
                <small style={{ display: 'block', marginTop: 3 }}>
                  👥 คู่ล่าสุด: {teammateNames.join(', ') || '-'}
                </small>
                <small style={{ display: 'block' }}>
                  ⚔️ ตรงข้ามล่าสุด: {opponentNames.join(', ') || '-'}
                </small>
                <small style={{
                  display: 'block',
                  marginTop: 3,
                  color: '#dc2626',
                  fontWeight: 700
                }}>
                  ⏱ {getStatus(player) === 'PLAY' ? 'เริ่มนับใหม่ ' : 'รอ '}
                  {formatWaitTime(player, now)}
                </small>
                <button
                  type="button"
                  className={`rmp-preview-edit-button ${
                    editingPreviewSlot === `${matchIndex}-${teamKey}-${playerIndex}`
                      ? 'is-open'
                      : ''
                  }`}
                  onClick={() => {
                    setPreviewEditSearch('');
                    setEditingPreviewSlot(current =>
                      current === `${matchIndex}-${teamKey}-${playerIndex}`
                        ? null
                        : `${matchIndex}-${teamKey}-${playerIndex}`
                    );
                  }}
                >
                  {editingPreviewSlot === `${matchIndex}-${teamKey}-${playerIndex}`
                    ? '✕ ปิดการแก้ไข'
                    : '✏️ เปลี่ยนผู้เล่น'}
                </button>
                {editingPreviewSlot === `${matchIndex}-${teamKey}-${playerIndex}` && (
                  <div className="rmp-preview-edit-picker">
                    <div className="rmp-preview-edit-search">
                      <span aria-hidden="true">🔎</span>
                      <input
                        type="search"
                        autoFocus
                        value={previewEditSearch}
                        placeholder="ค้นหาชื่อผู้เล่น..."
                        onChange={event => setPreviewEditSearch(event.target.value)}
                      />
                      {previewEditSearch && (
                        <button
                          type="button"
                          onClick={() => setPreviewEditSearch('')}
                          aria-label="ล้างชื่อที่ค้นหา"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <small className="rmp-preview-edit-hint">
                      ● ACTIVE แสดงเป็นอันดับแรก • กดชื่อเพื่อเปลี่ยนทันที
                    </small>
                    <div className="rmp-preview-edit-options">
                      {normalizedPlayers
                      .filter(candidate => getStatus(candidate) !== 'HOLD')
                      .filter(candidate =>
                        selectedPreviewRanks.length === 0 ||
                        selectedPreviewRanks.includes(
                          normalizePlayerRank(candidate.level_rank, candidate.point)
                        ) || Number(candidate.user_id) === Number(player.user_id)
                      )
                      .filter(candidate =>
                        !previewEditSearch.trim() ||
                        String(candidate.name || '')
                          .toLowerCase()
                          .includes(previewEditSearch.trim().toLowerCase())
                      )
                      .sort((left, right) =>
                        Number(getStatus(right) === 'ACTIVE') -
                          Number(getStatus(left) === 'ACTIVE') ||
                        getRankDistance(player, left) - getRankDistance(player, right) ||
                        Math.abs(Number(left.point || 0) - Number(player.point || 0)) -
                          Math.abs(Number(right.point || 0) - Number(player.point || 0))
                      )
                      .map(candidate => (
                        <button
                          type="button"
                          key={candidate.user_id}
                          className={
                            Number(candidate.user_id) === Number(player.user_id)
                              ? 'is-current'
                              : ''
                          }
                          onClick={() => editPreviewPlayer(
                            matchIndex,
                            teamKey,
                            playerIndex,
                            candidate.user_id
                          )}
                        >
                          <span>
                            {getStatus(candidate) === 'ACTIVE' ? '● ' : ''}
                            <b>{candidate.name}</b>
                          </span>
                          <small>
                            {normalizePlayerRank(candidate.level_rank, candidate.point)}
                            {' '}• {candidate.point} คะแนน • {getStatus(candidate)}
                          </small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </span>
              <span>
                {normalizePlayerRank(player.level_rank, player.point)} • {player.point}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPreviewRankSelector = () => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <strong>เลือก Rank สำหรับ Preview</strong>
        <small>{selectedPreviewRanks.length}/4 Rank</small>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 7 }}>
        {RANK_ORDER.map(rank => {
          const selected = selectedPreviewRanks.includes(rank);
          const playersInRank = normalizedPlayers.filter(player =>
            normalizePlayerRank(player.level_rank, player.point) === rank
          );
          const activeCount = playersInRank.filter(
            player => getStatus(player) === 'ACTIVE'
          ).length;
          const playCount = playersInRank.filter(
            player => getStatus(player) === 'PLAY'
          ).length;
          return (
            <button
              type="button"
              key={rank}
              aria-pressed={selected}
              onClick={() => togglePreviewRank(rank)}
              style={{
                padding: '6px 11px',
                borderRadius: 999,
                border: selected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                color: selected ? '#fff' : '#334155',
                background: selected ? '#2563eb' : '#fff'
              }}
            >
              {selected ? '✓ ' : ''}{rank} •{' '}
              <span style={{ color: selected ? '#dbeafe' : '#2563eb', fontWeight: 800 }}>
                A {activeCount}
              </span>
              {' '}•{' '}
              <span style={{ color: selected ? '#fee2e2' : '#dc2626', fontWeight: 800 }}>
                P {playCount}
              </span>
            </button>
          );
        })}
        {selectedPreviewRanks.length > 0 && (
          <button type="button" onClick={() => setSelectedPreviewRanks([])}>
            ล้าง • ใช้ทุก Rank
          </button>
        )}
      </div>
      <small>
        {selectedPreviewRanks.length === 0
          ? 'ยังไม่เลือก: ระบบใช้ทุก Rank'
          : `ใช้เฉพาะ ${selectedPreviewRanks.join(', ')}`}
        {' '}• A = ACTIVE • P = PLAY
      </small>
    </div>
  );

  const createdMatches = lastResult
    ? Array.isArray(lastResult.matches) && lastResult.matches.length > 0
      ? lastResult.matches
      : [lastResult]
    : [];

  const longestWaitingActivePlayers = normalizedPlayers
    .filter(player => ['ACTIVE', 'QUEUE', 'PLAY'].includes(getStatus(player)))
    .sort((left, right) =>
      getQueueStartedAt(left) - getQueueStartedAt(right) ||
      Number(left.user_id) - Number(right.user_id)
    )
    .slice(0, waitingAlertLimit);

  const getGenerateButtonLabel = () => {
    if (loading || aiAutoLoading) {
      if (mode === 'AI_RANDOM') {
        return '🤖 AI กำลังวิเคราะห์และสร้าง Queue...';
      }

      return mode === 'PRIORITY_SELECT' &&
        selectedManualPlayers.length < 4
        ? 'กำลังสุ่มผู้เล่นที่เหลือ...'
        : `กำลังสร้าง ${matchCount} Match...`;
    }

    if (mode === 'PRIORITY_SELECT') {
      if (selectedManualPlayers.length === 0) {
        return '🎲 เลือกอย่างน้อย 1 คนเพื่อ Random';
      }

      if (selectedManualPlayers.length < 4) {
        return (
          `🎲 Random อีก ${4 - selectedManualPlayers.length} คน ` +
          'และสร้าง Queue'
        );
      }

      return '➕ สร้าง Queue จากผู้เล่น 4 คน';
    }

    if (mode === 'AI_RANDOM') {
      return aiAutoEnabled
        ? `🤖 AI รอสร้าง Queue อัตโนมัติครั้งละ ${matchCount} Match`
        : `🤖 สร้าง Queue ${matchCount} Match จาก AI`;
    }

    if (mode === 'PICK_ONE' && aiAutoEnabled) {
      return '🤖 Pick One AI Auto กำลังรอสร้าง Queue';
    }

    if (supportsMultipleMatches && matchCount > 1) {
      return `➕ สร้าง Queue ${matchCount} Match`;
    }

    return '➕ สร้าง Queue';
  };
  void getGenerateButtonLabel;

  if (isMonitorAi) {
    const missingPlayers = Math.max(
      0,
      requiredPlayerCount - aiOrderedActivePlayers.length
    );

    return (
      <aside
        className={`rmp-monitor-ai ${aiAutoEnabled ? 'is-running' : 'is-stopped'}`}
        aria-label="AI Random อัตโนมัติ"
      >
        <div className="rmp-monitor-ai-title">
          <span className={`rmp-monitor-ai-dot ${aiAutoLoading ? 'is-loading' : ''}`} />
          <span>
            <strong>🤖 AI Random</strong>
            <small>
              ACTIVE {aiOrderedActivePlayers.length} คน
              {missingPlayers > 0 ? ` • รออีก ${missingPlayers}` : ' • พร้อมจัด'}
            </small>
          </span>
        </div>

        <div className="rmp-monitor-ai-count" aria-label="จำนวน Match ต่อรอบ">
          {[1, 3].map(count => (
            <button
              type="button"
              key={count}
              className={matchCount === count ? 'selected' : ''}
              disabled={aiAutoEnabled || aiAutoLoading}
              onClick={() => {
                setMatchCount(count);
                setError('');
                setAiAutomationStatus('');
                aiProcessedSignatureRef.current = '';
              }}
            >
              {count} Match
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`rmp-monitor-ai-toggle ${aiAutoEnabled ? 'stop' : 'start'}`}
          onClick={() => {
            const nextEnabled = !aiAutoEnabled;
            setAiAutoEnabled(nextEnabled);
            setError('');
            setAiAutomationStatus(
              nextEnabled
                ? `เริ่ม AI สร้างครั้งละ ${matchCount} Match`
                : 'หยุด AI Random แล้ว'
            );
            aiProcessedSignatureRef.current = '';
          }}
        >
          {aiAutoEnabled ? '■ หยุด' : '▶ เริ่ม'}
        </button>

        <span className="rmp-monitor-ai-status" title={aiAutomationStatus || undefined}>
          {aiAutoLoading
            ? 'กำลังวิเคราะห์...'
            : aiAutoEnabled
              ? aiAutomationStatus || 'กำลังทำงาน'
              : 'หยุดอยู่'}
        </span>

        {error && <span className="rmp-monitor-ai-error">⚠ {error}</span>}
      </aside>
    );
  }

  return (
    <section
      className="section-card random-match-panel"
      data-feature-version="daily-play-style-v3"
    >
      {waitingAlertVisible ? (
        <aside
          role="status"
          aria-label="ผู้เล่นที่รอนานที่สุด"
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 990,
            width: 'min(360px, calc(100vw - 32px))',
            padding: 14,
            border: '1px solid #fecaca',
            borderRadius: 14,
            background: '#fff7f7',
            boxShadow: '0 14px 35px rgba(127, 29, 29, 0.2)'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 10
          }}>
            <div>
              <strong style={{ color: '#991b1b' }}>⏱ สถานะและเวลารอผู้เล่น</strong>
              <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
                {[5, 10].map(limit => (
                  <button
                    type="button"
                    key={limit}
                    onClick={() => setWaitingAlertLimit(limit)}
                    style={{ fontWeight: waitingAlertLimit === limit ? 700 : 400 }}
                  >
                    Top {limit}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              aria-label="ปิดรายการผู้เล่นที่รอนาน"
              onClick={() => setWaitingAlertVisible(false)}
            >
              ✕
            </button>
          </div>

          <ol style={{ margin: '10px 0 0', paddingLeft: 24 }}>
            {longestWaitingActivePlayers.map(player => (
              <li key={player.user_id} style={{ marginTop: 6 }}>
                <b>{player.name}</b>{' '}
                <span>
                  Rank {normalizePlayerRank(player.level_rank, player.point)}
                </span>{' '}
                <span style={{ color: '#1d4ed8', fontWeight: 700 }}>
                  • {getPlayerMatchStatusLabel(player)}
                </span>{' '}
                <strong style={{ color: '#dc2626' }}>
                  • {getStatus(player) === 'PLAY' ? 'เริ่มนับใหม่ ' : 'รอ '}
                  {formatWaitTime(player, now)}
                </strong>
              </li>
            ))}
          </ol>
        </aside>
      ) : (
        <button
          type="button"
          onClick={() => setWaitingAlertVisible(true)}
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 990,
            color: '#991b1b'
          }}
        >
          ⏱ ดูคนรอนาน
        </button>
      )}

      <div className="rmp-heading">
        <div>
          <h3>🎲 Matchmaking</h3>
          <p>เลือกวิธีจัดผู้เล่น ระบบจะสร้าง Queue ใน transaction เดียว</p>
        </div>
        <div className="rmp-player-counts">
          <span className="rmp-active-count">
            ACTIVE {activePlayers.length} คน
          </span>
          {playingPlayers.length > 0 && (
            <span className="rmp-playing-count">
              PLAY {playingPlayers.length} คน
            </span>
          )}
        </div>
      </div>

      <div className="rmp-sticky-mode-bar">
      <div className="rmp-mode-grid">
        {MODES.map(item => (
          <label
            key={item.value}
            className={`rmp-mode-card ${mode === item.value ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="matchmaking-mode"
              value={item.value}
              checked={mode === item.value}
              onChange={() => {
                setMode(item.value);
                setApprovePlaying(false);
                setAiAutoEnabled(false);
                setAiAutomationStatus('');
                setAiRecommendation(null);
                setAdvancedSettingsOpen(false);
                setExpandedInfoPanel(null);
                aiProcessedSignatureRef.current = '';
                pickOneAttemptedIdsRef.current.clear();
                if (item.value === 'PRIORITY_SELECT') {
                  setPriority('WAIT_LONGEST');
                }
                if (item.value === 'AI_RANDOM') {
                  setPriority('FIFO');
                  setPlayStyle('ANY');
                  setMatchCount(1);
                }
                if (!AUTOMATIC_MODES.has(item.value)) setMatchCount(1);
                resetInputs();
                setLastResult(null);
                setError('');
              }}
            />
            <span>
              <strong>{item.label}</strong>
              <small>{item.description}</small>
            </span>
          </label>
        ))}
      </div>
      </div>

      {(mode === 'AI_RANDOM' || mode === 'PICK_ONE') && (
        <div className="rmp-ai-auto-panel">
          <div className="rmp-ai-auto-heading">
            <div>
              <strong>
                {mode === 'PICK_ONE'
                  ? '🤖 Pick One AI Auto'
                  : '🤖 AI Match แนะนำแบบเรียลไทม์'}
              </strong>
              <small>
                {mode === 'PICK_ONE'
                  ? 'เลือกผู้เล่นหลัก 1 คน แล้ว AI จะค้นหาอีก 3 คนที่คะแนนใกล้เคียงที่สุด พร้อมจัดผลรวมทีมให้สูสี'
                  : 'ยึดผู้เล่น ACTIVE ที่มาถึงก่อน แล้วค้นหาจากผู้เล่นทั้งก๊วน โดยให้คะแนนรายบุคคลใกล้กันก่อน แล้วจึงให้ผลรวมทีมสูสี (คู่ล็อกคะแนนต่างกันจะยึดผลรวมทีมก่อน)'}
              </small>
            </div>

            <label className="rmp-ai-auto-switch">
              <input
                type="checkbox"
                checked={aiAutoEnabled}
                disabled={mode === 'PICK_ONE' && !pickedPlayer}
                onChange={event => {
                  setAiAutoEnabled(event.target.checked);
                  setAiAutomationStatus('');
                  setError('');
                  aiProcessedSignatureRef.current = '';
                  pickOneAttemptedIdsRef.current.clear();
                }}
              />
              <span>
                {mode === 'PICK_ONE'
                  ? 'สร้าง Queue เมื่อพบคะแนนใกล้เคียง'
                  : 'สร้าง Queue อัตโนมัติ (ค่าเริ่มต้นปิด)'}
              </span>
            </label>
          </div>

          <div
            className="rmp-ai-live-status"
            role="status"
            aria-live="polite"
          >
            <span className={aiAutoLoading ? 'is-live' : ''} />
            {aiAutomationStatus ||
              'ระบบพร้อมตรวจผู้เล่น ACTIVE แบบเรียลไทม์'}
          </div>

          {(mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]) && (
            <div className="rmp-ai-anchor">
              <span>
                {mode === 'PICK_ONE' ? 'ผู้เล่นหลัก Pick One' : 'มาก่อน ลำดับ #1'}
              </span>
              <strong>
                {(mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]).name}
              </strong>
              <span>
                {Number((mode === 'PICK_ONE' ? pickedPlayer : aiOrderedActivePlayers[0]).point || 0)} คะแนน
              </span>
              {mode === 'AI_RANDOM' && (
                <span>
                  รอ {formatWaitTime(aiOrderedActivePlayers[0], now)}
                </span>
              )}
            </div>
          )}

     
        </div>
      )}

      <button
        type="button"
        className={`rmp-advanced-settings-toggle ${advancedSettingsOpen ? 'is-open' : ''
          }`}
        aria-expanded={advancedSettingsOpen}
        onClick={() => {
          setAdvancedSettingsOpen(current => !current);
          if (advancedSettingsOpen) setExpandedInfoPanel(null);
        }}
      >
        <span>⚙️ ตั้งค่าเพิ่มเติม</span>
        <small>
          {priority === 'FIFO' ? 'มาก่อน' : priority === 'LEAST_GAMES' ? 'เล่นน้อย' : 'รอนาน'}
          {' • '}ช่วงคะแนน {pointRange}
        </small>
        <b>{advancedSettingsOpen ? '▲ ซ่อน' : '▼ เปิด'}</b>
      </button>

      {advancedSettingsOpen && (
        <div className="rmp-advanced-settings-panel">
          {mode !== 'MANUAL_4' && mode !== 'AI_RANDOM' && (
            <div className="rmp-field">
              <label htmlFor="matchmaking-priority">
                {mode === 'PRIORITY_SELECT'
                  ? 'เรียงรายชื่อผู้เล่น'
                  : 'ลำดับความสำคัญ'}
              </label>
              <select
                id="matchmaking-priority"
                value={priority}
                onChange={event => {
                  setPriority(event.target.value);
                  setError('');
                }}
              >
                {mode !== 'PRIORITY_SELECT' && (
                  <option value="FIFO">มาก่อน ได้เล่นก่อน (FIFO)</option>
                )}
                <option value="WAIT_LONGEST">
                  รอนานที่สุด → มาก่อน
                </option>
                <option value="LEAST_GAMES">
                  เล่นน้อยที่สุด → มาก่อน
                </option>
              </select>
            </div>
          )}

          <div
            className="rmp-detail-toggle-bar"
            role="group"
            aria-label="เปิดดูรายละเอียดเงื่อนไขจัด Match"
          >
            <button
              type="button"
              className={`rmp-detail-icon-button ${expandedInfoPanel === 'mood' ? 'is-open' : ''
                }`}
              aria-expanded={expandedInfoPanel === 'mood'}
              aria-controls="rmp-mood-detail"
              title={`Mood: ${getPlayStyleLabel(playStyle)} • กดเพื่อดูหรือเปลี่ยน`}
              onClick={() => setExpandedInfoPanel(current => (
                current === 'mood' ? null : 'mood'
              ))}
            >
              <span className="rmp-detail-icon" aria-hidden="true">🎭</span>
              <small>Mood</small>
            </button>

            {mode !== 'MANUAL_4' && (
              <>
                <button
                  type="button"
                  className={`rmp-detail-icon-button ${expandedInfoPanel === 'rank' ? 'is-open' : ''
                    }`}
                  aria-expanded={expandedInfoPanel === 'rank'}
                  aria-controls="rmp-rank-detail"
                  title="กดดูขั้นการขยาย Rank และช่วงคะแนน"
                  onClick={() => setExpandedInfoPanel(current => (
                    current === 'rank' ? null : 'rank'
                  ))}
                >
                  <span className="rmp-detail-icon" aria-hidden="true">🎯</span>
                  <small>Rank</small>
                </button>

                <button
                  type="button"
                  className={`rmp-detail-icon-button ${expandedInfoPanel === 'repeat' ? 'is-open' : ''
                    }`}
                  aria-expanded={expandedInfoPanel === 'repeat'}
                  aria-controls="rmp-repeat-detail"
                  title="กดดูเงื่อนไขป้องกันคู่ซ้ำ"
                  onClick={() => setExpandedInfoPanel(current => (
                    current === 'repeat' ? null : 'repeat'
                  ))}
                >
                  <span className="rmp-detail-icon" aria-hidden="true">🔁</span>
                  <small>คู่ซ้ำ</small>
                </button>
              </>
            )}
          </div>

          {/* แสดงตัวเลือก Mood เมื่อกดไอคอน โดยค่าที่เลือกจะไม่หายตอนย่อ */}
          <div
            id="rmp-mood-detail"
            className="rmp-play-style-panel rmp-collapsible-detail"
            data-testid="daily-play-style-selector"
            hidden={expandedInfoPanel !== 'mood'}
          >
            <div className="rmp-option-heading">
              <strong>Mood ของวันนี้ที่ต้องการตีแบด</strong>
              <small>
                {mode === 'AI_RANDOM'
                  ? 'AI RANDOM ค้นหาจาก ACTIVE ทั้งหมด แต่จะจัดผู้เล่น Mood เดียวกันให้ครบ 4 คนก่อน'
                  : mode === 'MANUAL_4' || mode === 'PRIORITY_SELECT'
                    ? 'โหมดนี้อนุญาตให้เลือกผู้เล่นต่าง Mood และอนุญาตให้มือ S/P เล่นร่วมกับ BG ได้'
                    : (
                      <>
                        เมื่อเลือก “ทุก Mood” ระบบจะจัดผู้เล่น Mood เดียวกันก่อน
                        และไม่ให้มือ S/P อยู่ Match เดียวกับ BG
                      </>
                    )}
              </small>
            </div>

            <div className="rmp-play-style-options">
              {PLAY_STYLE_OPTIONS.map(option => {
                const styleActiveCount = option.value === 'ANY'
                  ? activeStyleCounts.totalActive
                  : activeStyleCounts.counts[option.value] || 0;

                return (
                  <button
                    type="button"
                    key={option.value}
                    className={playStyle === option.value ? 'selected' : ''}
                    aria-pressed={playStyle === option.value}
                    disabled={
                      mode === 'AI_RANDOM' && option.value !== 'ANY'
                    }
                    onClick={() => {
                      setPlayStyle(option.value);
                      resetInputs();
                      setLastResult(null);
                      setError('');
                    }}
                  >
                    <span>{option.icon}</span>
                    <span>
                      <strong>{option.label}</strong>
                      <small>{option.description}</small>
                      <em className="rmp-style-active-badge">
                        ACTIVE {styleActiveCount}
                      </em>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="rmp-style-eligible-count">
              {mode === 'AI_RANDOM'
                ? `AI ใช้ ACTIVE ทั้งหมด ${activePlayers.length} คน • จัด Mood เดียวกันก่อน`
                : mode === 'MANUAL_4' || mode === 'PRIORITY_SELECT'
                  ? `ข้อยกเว้น: เลือกต่าง Mood และเลือก S/P ร่วมกับ BG ได้`
                  : `ผู้เล่นตรง Mood: ACTIVE ${styleMatchingActiveCount} คน`}
              {playStyle !== 'ANY' && (
                <span>
                  {styleMatchingActiveCount < minimumStylePlayers
                    ? (
                      <>
                        {' '}• ต้องมีอย่างน้อย {minimumStylePlayers} คน
                        จึงจะสร้าง Match ได้
                      </>
                    )
                    : (
                      <>
                        {' '}• แสดงเฉพาะผู้เล่นที่เลือก Mood นี้
                      </>
                    )}
                </span>
              )}
              {playStyle === 'ANY' && (
                <span>
                  {' '}• แสดง ACTIVE ทั้งหมด {activePlayers.length} คน
                </span>
              )}
              {activeStyleCounts.missingStyle > 0 && (
                <span className="rmp-style-missing-count">
                  {' '}• ยังไม่เลือก Mood วันนี้{' '}
                  {activeStyleCounts.missingStyle} คน
                </span>
              )}
            </div>
          </div>

          {mode !== 'MANUAL_4' && (
            <>
              <div
                id="rmp-rank-detail"
                className="rmp-progressive-rule rmp-collapsible-detail"
                hidden={expandedInfoPanel !== 'rank'}
              >
                <div>
                  <strong>🎯 จับ Rank เดียวกันและคะแนนใกล้กันก่อน</strong>
                  <small>
                    หากยังไม่ครบ 4 คน ระบบจะค่อย ๆ ขยาย Rank แล้วไล่ช่วงคะแนนตามลำดับ
                    โดยไม่บังคับสร้าง Match ที่คะแนนทั้งกลุ่มห่างเกิน 300
                  </small>
                </div>
                <div className="rmp-progressive-steps" aria-label="ลำดับช่วงคะแนน">
                  {[20, 40, 80, 100, 150, 200, 250, 300].map(step => (
                    <span key={step}>≤ {step}</span>
                  ))}
                </div>
              </div>

              <div
                id="rmp-repeat-detail"
                className="rmp-repeat-rule rmp-collapsible-detail"
                hidden={expandedInfoPanel !== 'repeat'}
              >
                <strong>🔁 ป้องกันคู่ซ้ำ</strong>
                <span>
                  Pick One และ AI Random จะหลีกเลี่ยงทุกคนที่เพิ่งอยู่ Match เดียวกันใน
                  2 เกมล่าสุดก่อน หากติดซ้ำ ระบบจะขยับคะแนนและ Rank ขึ้น/ลงทีละช่วง
                  แล้วเลือกชุดที่ใกล้ที่สุด ส่วน Locked Pair อยู่ทีมเดียวกันซ้ำได้ตลอด
                </span>
              </div>
            </>
          )}

          {(supportsMultipleMatches || usesPointRange) && (
            <div className="rmp-settings-grid">
              {usesPointRange && (
                <div className="rmp-option-panel">
                  <div className="rmp-option-heading">
                    <strong>
                      {usesIndividualPointRange
                        ? 'เกณฑ์คะแนนข้ามทีมและภายในทีม'
                        : 'ช่วงคะแนนจากผู้เล่นคนแรก'}
                    </strong>
                    <small>
                      {usesIndividualPointRange
                        ? 'ทั้งคู่แข่งตำแหน่งเดียวกันข้ามทีม และผู้เล่น 2 คนภายในแต่ละทีม ต้องมีคะแนนห่างไม่เกินค่านี้'
                        : 'คนแรกเลือกตาม Priority ด้านบน ส่วนอีก 3 คนต้องมีคะแนนต่างจากคนแรกไม่เกินค่านี้'}
                    </small>
                  </div>

                  <div className="rmp-range-options">
                    {POINT_RANGES.map(range => (
                      <button
                        type="button"
                        key={range}
                        className={pointRange === range ? 'selected' : ''}
                        aria-pressed={pointRange === range}
                        onClick={() => {
                          setPointRange(range);
                          setLastResult(null);
                          setError('');
                        }}
                      >
                        ± ไม่เกิน {range}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {supportsMultipleMatches && (
                <div className="rmp-option-panel">
                  <div className="rmp-option-heading">
                    <strong>จำนวน Match ที่ต้องการสร้าง</strong>
                    <small>
                      {mode === 'AI_RANDOM'
                        ? 'กำหนดจำนวน Queue ต่อหนึ่งรอบของ AI ระบบจะรอจนมี ACTIVE ครบตามจำนวน'
                        : 'ผู้เล่นจะไม่ซ้ำกันระหว่าง Match ที่สร้างพร้อมกัน'}
                    </small>
                  </div>

                  {mode === 'AI_RANDOM' && (
                    <div className="rmp-ai-count-presets">
                      {[1, 5, 10, 15].map(count => (
                        <button
                          type="button"
                          key={count}
                          className={matchCount === count ? 'selected' : ''}
                          onClick={() => {
                            setMatchCount(count);
                            setError('');
                            setLastResult(null);
                            setAiAutomationStatus('');
                            aiProcessedSignatureRef.current = '';
                          }}
                        >
                          {count} Match
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="rmp-match-count-control">
                    <button
                      type="button"
                      aria-label="ลดจำนวน Match"
                      disabled={matchCount <= 1}
                      onClick={() => setMatchCount(current => Math.max(1, current - 1))}
                    >
                      −
                    </button>
                    <select
                      aria-label="จำนวน Match"
                      value={matchCount}
                      onChange={event => setMatchCount(Number(event.target.value))}
                    >
                      {Array.from({ length: maxMatchCount }, (_, index) => index + 1)
                        .map(count => (
                          <option value={count} key={count}>
                            {count} Match
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      aria-label="เพิ่มจำนวน Match"
                      disabled={matchCount >= maxMatchCount}
                      onClick={() => setMatchCount(
                        current => Math.min(maxMatchCount, current + 1)
                      )}
                    >
                      ＋
                    </button>
                  </div>

                  <div className="rmp-batch-hint">
                    ใช้ผู้เล่น {requiredPlayerCount} คน • สร้างได้สูงสุด{' '}
                    {availableMatchCount} Match
                    {' '}จากผู้เล่น ACTIVE ปัจจุบัน
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rmp-sticky-status-tools">
        <div className="rmp-sticky-status-row">
      {canApprovePlaying && (
        <label className={`rmp-approve ${approvePlaying ? 'enabled' : ''}`}>
          <input
            type="checkbox"
            checked={approvePlaying}
            onChange={event => handleApproveChange(event.target.checked)}
          />
          <span>
            <strong>Approve ทุกสถานะ</strong>
            <small>
              อนุญาตผู้เล่นทุกสถานะ เช่น PLAY, QUEUE และ CHECKOUT
              เพื่อจัด Match ล่วงหน้า รวมถึง Locked Pair แต่ไม่รวม HOLD
            </small>
          </span>
        </label>
      )}

          <button
            type="button"
            className={`rmp-hold-toggle ${holdManagerOpen ? 'is-open' : ''}`}
            onClick={() => setHoldManagerOpen(open => !open)}
          >
            ⏸ HOLD {heldPlayers.length} คน {holdManagerOpen ? '▲' : '▼'}
          </button>
        </div>

      {holdManagerOpen && (
      <section className="rmp-hold-manager">
        <div className="rmp-hold-heading">
          <div>
            <strong>⏸ พักผู้เล่นชั่วคราว (HOLD)</strong>
            <small>ค้นหาชื่อแล้วเปลี่ยน ACTIVE เป็น HOLD หรือเรียกกลับเป็น ACTIVE</small>
          </div>
          <span>HOLD {heldPlayers.length} คน</span>
        </div>

        <div className="rmp-hold-search-wrap">
          <span aria-hidden="true">🔎</span>
          <input
            type="search"
            value={statusSearchKeyword}
            placeholder="ค้นหาชื่อผู้เล่นเพื่อเปลี่ยนสถานะ HOLD / ACTIVE"
            onChange={event => {
              setStatusSearchKeyword(event.target.value);
              setStatusUpdateMessage(null);
            }}
          />
          {statusSearchKeyword && (
            <button
              type="button"
              className="rmp-hold-clear"
              onClick={() => {
                setStatusSearchKeyword('');
                setStatusUpdateMessage(null);
              }}
              aria-label="ล้างคำค้นหา"
            >
              ×
            </button>
          )}
        </div>

        <div className="rmp-hold-results">
          {visibleStatusPlayers.length === 0 && (
            <div className="rmp-hold-empty">
              {statusSearchKeyword.trim()
                ? 'ไม่พบชื่อผู้เล่น'
                : 'ยังไม่มีผู้เล่นสถานะ HOLD'}
            </div>
          )}

          {visibleStatusPlayers.map(player => {
            const currentStatus = getStatus(player);
            const canSetHold = currentStatus === 'ACTIVE';
            const canSetActive = currentStatus === 'HOLD';
            const lockedPartner = getLockedPartner(player);

            return (
              <div className="rmp-hold-player" key={player.user_id}>
                <div className="rmp-hold-player-info">
                  <strong>{player.name}</strong>
                  <span>
                    {normalizePlayerRank(player.level_rank, player.point)} • {Number(player.point || 0)} คะแนน
                  </span>
                  {lockedPartner && (
                    <small>🔒 Locked Pair: {lockedPartner.name}</small>
                  )}
                </div>

                <span className={`rmp-hold-current status-${currentStatus.toLowerCase()}`}>
                  {currentStatus}
                </span>

                {canSetHold && (
                  <button
                    type="button"
                    className="rmp-set-hold"
                    disabled={statusUpdatingId === Number(player.user_id)}
                    onClick={() => updateAvailabilityStatus(player, 'HOLD')}
                  >
                    {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก...' : '⏸ HOLD'}
                  </button>
                )}

                {canSetActive && (
                  <button
                    type="button"
                    className="rmp-set-active"
                    disabled={statusUpdatingId === Number(player.user_id)}
                    onClick={() => updateAvailabilityStatus(player, 'ACTIVE')}
                  >
                    {statusUpdatingId === Number(player.user_id) ? 'กำลังบันทึก...' : '▶ ACTIVE'}
                  </button>
                )}

                {!canSetHold && !canSetActive && (
                  <small className="rmp-hold-blocked">
                    ต้องออกจาก {currentStatus} ก่อน
                  </small>
                )}
              </div>
            );
          })}
        </div>

        {statusUpdateMessage && (
          <div className={`rmp-hold-message ${statusUpdateMessage.type}`} role="status">
            {statusUpdateMessage.text}
          </div>
        )}
      </section>
      )}
      </div>

      <style>{`
        .rmp-sticky-mode-bar {
          position: sticky;
          top: 8px;
          z-index: 45;
          padding: 7px;
          border: 1px solid #dbeafe;
          border-radius: 14px;
          background: rgba(248, 250, 252, .96);
          box-shadow: 0 7px 20px rgba(15, 23, 42, .1);
          backdrop-filter: blur(8px);
        }
        .rmp-sticky-mode-bar .rmp-mode-grid {
          display: grid;
          grid-template-columns: repeat(${MODES.length}, minmax(150px, 1fr));
          gap: 7px;
          overflow-x: auto;
        }
        .rmp-sticky-mode-bar .rmp-mode-card { min-width: 150px; }
        .rmp-sticky-status-tools {
          position: sticky;
          top: 92px;
          z-index: 44;
          padding: 7px;
          border: 1px solid #fde68a;
          border-radius: 14px;
          background: rgba(255, 251, 235, .97);
          box-shadow: 0 7px 20px rgba(120, 53, 15, .1);
          backdrop-filter: blur(8px);
        }
        .rmp-sticky-status-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: stretch;
          gap: 8px;
        }
        .rmp-sticky-status-row .rmp-approve { margin: 0; }
        .rmp-hold-toggle {
          min-width: 145px;
          padding: 9px 12px;
          border: 1px solid #fbbf24;
          border-radius: 11px;
          color: #92400e;
          background: #fff7d6;
          font-weight: 900;
          cursor: pointer;
        }
        .rmp-hold-toggle.is-open { color: #fff; background: #d97706; }
        .rmp-sticky-status-tools .rmp-hold-manager { margin-top: 8px; max-height: 52vh; overflow: auto; }
        @media (max-width: 760px) {
          .rmp-sticky-mode-bar { top: 6px; padding: 5px; }
          .rmp-sticky-mode-bar .rmp-mode-grid {
            grid-template-columns: repeat(${MODES.length}, minmax(126px, 1fr));
          }
          .rmp-sticky-mode-bar .rmp-mode-card {
            min-width: 126px;
            min-height: 62px;
            padding: 8px;
          }
          .rmp-sticky-mode-bar .rmp-mode-card small { font-size: 10px; }
          .rmp-sticky-status-tools { top: 82px; padding: 5px; }
          .rmp-sticky-status-row { grid-template-columns: minmax(0, 1fr) auto; }
          .rmp-sticky-status-row .rmp-approve { padding: 8px; }
          .rmp-sticky-status-row .rmp-approve small { display: none; }
          .rmp-hold-toggle { min-width: 112px; padding: 8px 7px; font-size: 11px; }
          .rmp-preview-backdrop {
            place-items: center !important;
            width: 100vw !important;
            height: 100dvh !important;
            padding: 6px !important;
            overflow: auto;
            z-index: 30000 !important;
          }
          .rmp-preview-modal {
            width: calc(100vw - 12px) !important;
            max-width: 960px !important;
            height: auto !important;
            max-height: calc(100dvh - 12px) !important;
            padding: 10px 10px max(18px, env(safe-area-inset-bottom)) !important;
            border: 3px solid #2563eb !important;
            border-radius: 16px !important;
            box-sizing: border-box;
            overflow-x: hidden !important;
            overflow-y: auto !important;
            box-shadow: 0 24px 70px rgba(15,23,42,.42), 0 0 0 4px rgba(147,197,253,.5) !important;
          }
          .rmp-preview-heading {
            position: sticky;
            top: -10px;
            z-index: 8;
            margin: -10px -10px 9px !important;
            padding: 10px;
            background: rgba(255,255,255,.97);
            box-shadow: 0 3px 10px rgba(15,23,42,.08);
          }
          .rmp-preview-list {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 8px !important;
            width: 100%;
            padding-bottom: 7px;
            overflow-x: visible;
          }
          .rmp-preview-match {
            width: 100%;
            min-width: 0;
            padding: 6px !important;
            border-radius: 10px !important;
          }
          .rmp-preview-match > div:first-child { margin-bottom: 4px !important; font-size: 10px; }
          .rmp-preview-match .rmp-result-teams {
            display: grid !important;
            grid-template-columns: 1fr !important;
            align-items: stretch;
            gap: 3px !important;
          }
          .rmp-preview-match .rmp-result-team { min-width: 0; padding: 4px; }
          .rmp-preview-match .rmp-result-player {
            grid-template-columns: 25px minmax(0,1fr) auto !important;
            gap: 3px !important;
            padding: 2px 1px;
            font-size: 8px;
            line-height: 1.15;
          }
          .rmp-preview-match .rmp-player-avatar,
          .rmp-preview-match .rmp-player-avatar.normal {
            width: 25px;
            min-width: 25px;
            height: 25px;
          }
          .rmp-preview-match .rmp-preview-edit-button {
            margin-top: 2px;
            padding: 3px 6px;
            font-size: 8px;
          }
          .rmp-preview-match .rmp-vs { align-self: center; padding: 1px 0; text-align: center; }
          .rmp-preview-match > div:last-child { margin-top: 4px !important; gap: 4px !important; }
          .rmp-preview-match > div:last-child button { padding: 5px 7px; font-size: 8px; }
        }
      `}</style>

      {mode === 'PICK_ONE' && (
        <div className="rmp-pick-one-layout">
          <div className="rmp-field rmp-pick-anchor">
            <label>ผู้เล่นหลักที่ต้องอยู่ใน Match</label>
            <PlayerSearchDropdown
              value={normalizeDropdownPlayer(pickedPlayer)}
              allPlayers={normalizeDropdownPlayers(pickOneAnchorPlayers)}
              usedIds={[]}
              sortMode="ZERO_GAMES_WAIT"
              onChange={player => {
                const playerStatus = getStatus(player);

                if (playerStatus === 'HOLD') {
                  setError(`${player.name} อยู่สถานะ HOLD และยังไม่พร้อมลงเล่น`);
                  return;
                }

                if (playerStatus !== 'ACTIVE' && !approvePlaying) {
                  setError(
                    `${player.name} อยู่สถานะ ${playerStatus} — กรุณาเปิด Approve ทุกสถานะก่อนเลือก`
                  );
                  return;
                }

                setPickedPlayer(player);
                setSecondPickedPlayer(null);
                setPickOnePlayers([]);
                setNearbySearchKeyword('');
                setLastResult(null);
                setError('');
                setAiRecommendation(null);
                setAiAutomationStatus('');
                aiProcessedSignatureRef.current = '';
                pickOneAttemptedIdsRef.current.clear();
              }}
            />

            {pickedPlayer && !pickedLockedPartner && (
              <div style={{ marginTop: 10 }}>
                <label>ผู้เล่นหลักคนที่ 2 (ไม่บังคับ • อยู่ Team เดียวกัน)</label>
                <PlayerSearchDropdown
                  value={normalizeDropdownPlayer(secondPickedPlayer)}
                  allPlayers={normalizeDropdownPlayers(
                    normalizedPlayers.filter(player =>
                      getStatus(player) !== 'HOLD' &&
                      Number(player.user_id) !== Number(pickedPlayer.user_id) &&
                      Number(player.user_id) !== Number(pickedLockedPartner?.user_id) &&
                      !getLockedPartner(player)
                    )
                  )}
                  usedIds={[pickedPlayer.user_id]}
                  sortMode="ZERO_GAMES_WAIT"
                  onChange={player => {
                    setSecondPickedPlayer(player);
                    setPickOnePlayers(current => current.slice(0, 2));
                    setError('');
                    setPreviewMatches([]);
                  }}
                />
                {secondPickedPlayer && (
                  <button
                    type="button"
                    onClick={() => setSecondPickedPlayer(null)}
                    style={{ marginTop: 6 }}
                  >
                    ใช้ผู้เล่นหลักเพียง 1 คน
                  </button>
                )}
              </div>
            )}

            {pickedPlayer && (
              <div className="rmp-pick-anchor-summary">

                <div className="rmp-pick-anchor-player">

                  <PlayerAvatar
                    player={pickedPlayer}
                    size="large"
                  />

                  <div className="rmp-pick-anchor-info">

                    <strong>
                      {pickedPlayer.name}
                    </strong>

                    <div className="rmp-pick-anchor-meta">

                      <span>
                        Rank {normalizePlayerRank(
                          pickedPlayer.level_rank,
                          pickedPlayer.point
                        ) || '-'}
                      </span>

                      <span>
                        {Number(
                          pickedPlayer.point || 0
                        )} คะแนน
                      </span>

                      <span className="rmp-pick-position">
                        #1 • Team 1
                      </span>

                    </div>

                  </div>

                </div>


                {pickedLockedPartner && (
                  <span className="locked">
                    #2 • Team 1 • 🔒 คู่ล็อก{' '}
                    {pickedLockedPartner.name}
                  </span>
                )}

              </div>
            )}
          </div>

          {pickedPlayer && (
            <div className="rmp-pick-nearby-panel">
              <div className="rmp-pick-nearby-heading">
                <div>
                  <strong>ผู้เล่น Rank ใกล้เคียง</strong>
                  <small>
                    แสดงทุกสถานะ • ACTIVE, QUEUE, PLAY, HOLD, CHECKOUT
                    {' '}• ภายในสถานะเรียงจำนวนเกม แล้วพิจารณา Rank และคะแนนใกล้เคียง
                  </small>
                </div>
                <span>
                  เลือกเพิ่ม {pickOnePlayers.length}/
                  {Math.max(
                    0,
                    4 - 1 -
                    (secondPickedPlayer ? 1 : 0) -
                    (pickedLockedPartner ? 1 : 0)
                  )}
                </span>
              </div>

              <div className="rmp-pick-nearby-controls">
                <div className="rmp-pick-nearby-search">
                  <span aria-hidden="true">🔎</span>
                  <input
                    type="search"
                    value={nearbySearchKeyword}
                    placeholder="ค้นหาชื่อผู้เล่นทุก Rank"
                    onChange={event => setNearbySearchKeyword(event.target.value)}
                  />
                  {nearbySearchKeyword && (
                    <button
                      type="button"
                      onClick={() => setNearbySearchKeyword('')}
                      aria-label="ล้างคำค้นหาผู้เล่น Rank ใกล้เคียง"
                    >
                      ×
                    </button>
                  )}
                </div>
                <label className="rmp-pick-nearby-sort">
                  <span>จำนวนเกม</span>
                  <select
                    value={nearbyGameSort}
                    onChange={event => setNearbyGameSort(event.target.value)}
                    aria-label="เรียงผู้เล่นตามจำนวนเกม"
                  >
                    <option value="ASC">น้อย → มาก</option>
                    <option value="DESC">มาก → น้อย</option>
                  </select>
                </label>
              </div>

              <div style={{ margin: '10px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong>เลือก Rank ผู้เล่นใกล้เคียง</strong>
                  <small>{selectedNearbyRanks.length}/4 Rank</small>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {RANK_ORDER.map(rank => {
                    const selected = selectedNearbyRanks.includes(rank);
                    const playersInRank = normalizedPlayers.filter(player =>
                      normalizePlayerRank(player.level_rank, player.point) === rank
                    );
                    const activeCount = playersInRank.filter(
                      player => getStatus(player) === 'ACTIVE'
                    ).length;
                    const playCount = playersInRank.filter(
                      player => getStatus(player) === 'PLAY'
                    ).length;
                    return (
                      <button
                        type="button"
                        key={rank}
                        aria-pressed={selected}
                        onClick={() => toggleNearbyRank(rank)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 999,
                          border: selected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                          color: selected ? '#fff' : '#334155',
                          background: selected ? '#2563eb' : '#fff'
                        }}
                      >
                        {selected ? '✓ ' : ''}{rank} •{' '}
                        <span style={{ color: selected ? '#dbeafe' : '#2563eb', fontWeight: 800 }}>
                          A {activeCount}
                        </span>
                        {' '}•{' '}
                        <span style={{ color: selected ? '#fee2e2' : '#dc2626', fontWeight: 800 }}>
                          P {playCount}
                        </span>
                      </button>
                    );
                  })}
                  {selectedNearbyRanks.length > 0 && (
                    <button type="button" onClick={() => setSelectedNearbyRanks([])}>
                      ล้าง • ใช้ทุก Rank
                    </button>
                  )}
                </div>
                <small style={{ display: 'block', marginTop: 5, color: '#64748b' }}>
                  A = ACTIVE • P = PLAY • จำนวนอัปเดตตามสถานะล่าสุด
                </small>
              </div>

              <div className="rmp-pick-nearby-scroll">
                {pickOneNearbyPlayers.length === 0 && (
                  <div className="rmp-pick-nearby-empty">
                    {nearbySearchKeyword.trim()
                      ? 'ไม่พบชื่อผู้เล่น'
                      : 'ไม่พบผู้เล่น'}
                  </div>
                )}

                {pickOneNearbyPlayers.map(player => {

                  const selected =
                    pickOnePlayers.some(
                      item =>
                        Number(item.user_id) ===
                        Number(player.user_id)
                    );

                  const rankDistance =
                    getRankDistance(
                      pickedPlayer,
                      player
                    );

                  const pointGap =
                    Math.abs(
                      Number(player.point || 0) -
                      Number(pickedPlayer.point || 0)
                    );

                  const lockedPartner =
                    getLockedPartner(player);

                  const teammateCount =
                    getRelationCount(
                      pairMap,
                      pickedPlayer,
                      player
                    );

                  const opponentCount =
                    getRelationCount(
                      opponentMap,
                      pickedPlayer,
                      player
                    );

                  const queueMatchNo =
                    getQueueMatchNo(player);

                  const queueMatchId =
                    getQueueMatchId(player);

                  const holdBlocked =
                    getStatus(player) === 'HOLD' ||
                    getStatus(lockedPartner) === 'HOLD';
                  const selectedIndex = pickOneOrderedPlayers.findIndex(
                    item => Number(item.user_id) === Number(player.user_id)
                  );

                  return (
                    <button
                      type="button"
                      key={player.user_id}
                      className={`rmp-pick-nearby-card ${selected ? 'selected' : ''}`}
                      aria-pressed={selected}
                      disabled={holdBlocked}
                      onClick={() => togglePickOneNearbyPlayer(player)}
                    >
                      <div className="rmp-pick-nearby-player">

                        <PlayerAvatar
                          player={player}
                          size="normal"
                        />

                        <span className="rmp-pick-nearby-name">
                          {player.name}
                        </span>

                      </div>
                      <span className="rmp-pick-nearby-meta">

                        <b>
                          {normalizePlayerRank(
                            player.level_rank,
                            player.point
                          ) || '-'}
                        </b>

                        <em>
                          {Number(player.point || 0)} คะแนน
                        </em>

                        <em className="rmp-pick-games">
                          🎮 {getPlayerGames(player)} เกม
                        </em>

                        <i
                          className={`rmp-pick-status status-${getStatus(player).toLowerCase()}`}
                        >
                          {getStatus(player) || '-'}
                        </i>

                      </span>

                      {(queueMatchNo || queueMatchId) && (
                        <span className="rmp-queue-match-badge">
                          ⏳ Match Queue #{queueMatchNo || queueMatchId}
                        </span>
                      )}
                      <small>
                        {rankDistance === 0
                          ? 'Rank เดียวกัน'
                          : `Rank ห่าง ${rankDistance} ระดับ`} • คะแนนห่าง {pointGap}
                      </small>
                      <span className="rmp-pick-history">
                        <b title="จำนวนครั้งที่เคยอยู่ทีมเดียวกับผู้เล่นหลัก">
                          👥 คู่ทีม {teammateCount}
                        </b>
                        <b title="จำนวนครั้งที่เคยอยู่ฝั่งตรงข้ามกับผู้เล่นหลัก">
                          ⚔ ตรงข้าม {opponentCount}
                        </b>
                      </span>
                      {lockedPartner && (
                        <small className="locked">🔒 Locked Pair: {lockedPartner.name}</small>
                      )}
                      <span className="rmp-pick-nearby-action">
                        {holdBlocked
                          ? '⏸ ผู้เล่นหรือคู่ล็อกอยู่ HOLD'
                          : selected
                            ? `✓ #${selectedIndex + 1} • Team ${selectedIndex < 2 ? 1 : 2}`
                            : '+ เลือก'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="rmp-pick-nearby-footer">
                <span>
                  แสดงทั้งหมด {pickOneNearbyCandidates.length} คน
                </span>
              </div>
              <div className="rmp-pick-team-rule">
                <strong>ลำดับ 1–2 = Team 1</strong>
                <span>ลำดับ 3–4 = Team 2</span>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'PRIORITY_SELECT' && (
        <div className="rmp-priority-select">
          <div className="rmp-priority-list-heading">
            <div>
              <strong>
                {priority === 'LEAST_GAMES'
                  ? '🎮 ผู้เล่นที่เล่นน้อยที่สุด'
                  : '⏱ ผู้เล่นที่รอนานที่สุด'}
              </strong>
              <small>
                ระบบเรียงตามอันดับคิว: สองอันดับแรกเป็น Team 1
                และสองอันดับถัดไปเป็น Team 2 ไม่อิงลำดับการกด
              </small>
            </div>
            <span className={selectedManualPlayers.length === 4 ? 'complete' : ''}>
              เลือกแล้ว {selectedManualPlayers.length}/4
            </span>
          </div>

          <input
            type="search"
            className="rmp-priority-search"
            value={priorityKeyword}
            placeholder="ค้นหาชื่อ คะแนน หรือระดับมือ"
            onChange={event => setPriorityKeyword(event.target.value)}
          />

          <div className="rmp-priority-player-list">
            {priorityPlayers.length === 0 && (
              <div className="rmp-priority-empty">ไม่พบผู้เล่น ACTIVE</div>
            )}

            {priorityPlayers.map((player, index) => {
              const selectedIndex = orderedPrioritySelections.findIndex(
                item => Number(item.user_id) === Number(player.user_id)
              );
              const selected = selectedIndex >= 0;
              const selectedTeam = selectedIndex < 2 ? 1 : 2;

              return (
                <button
                  type="button"
                  key={player.user_id}
                  className={[
                    'rmp-priority-player',
                    selected ? 'selected' : '',
                    selected ? `team-${selectedTeam}` : ''
                  ].filter(Boolean).join(' ')}
                  aria-pressed={selected}
                  onClick={() => togglePriorityPlayer(player)}
                >
                  <span className="rmp-priority-order">{index + 1}</span>
                  <span className="rmp-priority-name">
                    <strong>{player.name}</strong>
                    <small>
                      {player.level_rank || '-'} • {Number(player.point || 0)} คะแนน
                      {' • '}{getPlayStyleLabel(player.daily_play_style)}
                    </small>
                  </span>
                  <span className="rmp-priority-stat">
                    <small>เวลารอ</small>
                    <strong>{formatWaitTime(player, now)}</strong>
                  </span>
                  <span className="rmp-priority-stat">
                    <small>จำนวนเกม</small>
                    <strong>{getPlayerGames(player)} เกม</strong>
                  </span>
                  <span className="rmp-priority-check">
                    {selected
                      ? `✓ T${selectedTeam} • คนที่ ${selectedIndex + 1}`
                      : 'เลือก'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mode === 'MANUAL_4' && (
        <div className="rmp-field">
          <label>เลือกผู้เล่นแยกตามทีม</label>
          <div className="rmp-manual-teams">
            <div className="rmp-manual-team rmp-manual-team-one">
              <div className="rmp-manual-team-heading">
                <strong>Team 1</strong>
                <small>คนที่ 1 และคนที่ 2 อยู่ทีมเดียวกัน</small>
              </div>

              {[0, 1].map(index => {
                const player = manualPlayers[index];

                return (
                  <PlayerSearchDropdown
                    key={index}
                    value={normalizeDropdownPlayer(player)}
                    allPlayers={normalizeDropdownPlayers(orderedSelectablePlayers)}
                    usedIds={manualPlayers
                      .map(item => item?.user_id)
                      .filter(id => (
                        id &&
                        Number(id) !== Number(player?.user_id)
                      ))}
                    onChange={newPlayer => {
                      setManualPlayers(currentPlayers => {
                        const nextPlayers = [...currentPlayers];
                        nextPlayers[index] = newPlayer;
                        return nextPlayers;
                      });
                    }}
                  />
                );
              })}
            </div>

            <span className="rmp-manual-vs">VS</span>

            <div className="rmp-manual-team rmp-manual-team-two">
              <div className="rmp-manual-team-heading">
                <strong>Team 2</strong>
                <small>ผู้เล่นฝั่งตรงข้าม</small>
              </div>

              {[2, 3].map(index => {
                const player = manualPlayers[index];

                return (
                  <PlayerSearchDropdown
                    key={index}
                    value={normalizeDropdownPlayer(player)}
                    allPlayers={normalizeDropdownPlayers(orderedSelectablePlayers)}
                    usedIds={manualPlayers
                      .map(item => item?.user_id)
                      .filter(id => (
                        id &&
                        Number(id) !== Number(player?.user_id)
                      ))}
                    onChange={newPlayer => {
                      setManualPlayers(currentPlayers => {
                        const nextPlayers = [...currentPlayers];
                        nextPlayers[index] = newPlayer;
                        return nextPlayers;
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rmp-error" role="alert">
          ❌ {error}
        </div>
      )}

      {mode === 'PICK_ONE' && (
        <button
          type="button"
          className="rmp-generate-button"
          disabled={loading || pickOneOrderedPlayers.length !== 4}
          onClick={createQueueFromSelectedPlayers}
          style={{ marginBottom: 12 }}
        >
          {loading
            ? 'กำลังสร้าง Queue...'
            : `➕ สร้าง Queue จากผู้เล่นที่เลือก (${pickOneOrderedPlayers.length}/4)`}
        </button>
      )}

      {renderPreviewRankSelector()}

      <button
        type="button"
        className={`rmp-generate-button ${mode === 'PRIORITY_SELECT' &&
          selectedManualPlayers.length > 0 &&
          selectedManualPlayers.length < 4
          ? 'random-fill'
          : ''
          }`}
        disabled={
          loading ||
          previewLoading ||
          aiAutoLoading ||
          (
            (mode === 'AI_RANDOM' || mode === 'PICK_ONE') &&
            aiAutoEnabled
          ) ||
          lacksRequiredStylePlayers ||
          (AUTOMATIC_MODES.has(mode) &&
            activePlayers.length < requiredPlayerCount)
        }
        onClick={loadQueuePreviews}
      >
        {previewLoading ? 'กำลังสร้าง Preview...' : '👀 Preview Match ก่อนสร้าง Queue'}
      </button>

      <button
        type="button"
        className="rmp-generate-button"
        disabled={loading || previewLoading || !pickedPlayer}
        onClick={loadFixedPlayerPreviews}
        style={{ marginTop: 10 }}
      >
        🎯 Preview 3 Match โดยยึดผู้เล่นหลัก
      </button>

      {previewOpen && createPortal((
        <div
          className="rmp-preview-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setPreviewOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            width: '100vw',
            height: '100dvh',
            padding: 12,
            background: 'rgba(15, 23, 42, 0.7)'
          }}
        >
          <div
            className="rmp-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rmp-preview-title"
            style={{
              width: 'min(960px, calc(100vw - 24px))',
              maxWidth: '960px',
              height: 'auto',
              maxHeight: '90dvh',
              overflowX: 'hidden',
              overflowY: 'auto',
              boxSizing: 'border-box',
              padding: 20,
              border: '3px solid #2563eb',
              borderRadius: 18,
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.42), 0 0 0 4px rgba(147, 197, 253, 0.5)',
              background: '#fff',
              color: '#172033'
            }}
          >
            <div className="rmp-preview-heading" style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              alignItems: 'flex-start',
              marginBottom: 16
            }}>
              <div>
                <h3 id="rmp-preview-title" style={{ margin: 0 }}>
                  {previewType === 'FIXED_PLAYER'
                    ? `Preview โดยยึด ${pickedPlayer?.name || 'ผู้เล่นหลัก'}`
                    : 'Preview Match ที่พร้อมลง'}
                </h3>
                <small>
                  {previewType === 'FIXED_PLAYER'
                    ? 'สร้าง 3 Match • ใช้ได้ทุกสถานะยกเว้น HOLD'
                    : 'ผู้เล่น ACTIVE และ PLAY • เลือก Rank เดียวกันก่อน แล้วขยายเป็น Rank ใกล้กันเมื่อคนไม่พอ'}
                </small>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  disabled={previewLoading || previewCreatingIndex !== null}
                  onClick={refreshCurrentPreview}
                >
                  🔄 Refresh Preview
                </button>
                <button type="button" onClick={() => setPreviewOpen(false)}>
                  ✕ ปิด
                </button>
              </div>
            </div>

            {renderPreviewRankSelector()}

            {previewAlert && (
              <div
                role="alert"
                style={{
                  marginBottom: 14,
                  padding: '10px 12px',
                  borderRadius: 10,
                  color: previewAlert.type === 'error' ? '#991b1b' : '#166534',
                  background: previewAlert.type === 'error' ? '#fee2e2' : '#dcfce7',
                  border: `1px solid ${previewAlert.type === 'error' ? '#fecaca' : '#bbf7d0'}`
                }}
              >
                {previewAlert.type === 'error' ? '❌' : '✅'} {previewAlert.text}
              </div>
            )}

            <div className="rmp-preview-list" style={{ display: 'grid', gap: 14 }}>
              {previewLoading && (
                <div style={{ padding: 28, textAlign: 'center' }}>
                  ⏳ กำลังวิเคราะห์ผู้เล่น ACTIVE และสร้าง Preview...
                </div>
              )}
              {!previewLoading && previewMatches.length === 0 && (
                <div style={{ padding: 28, textAlign: 'center' }}>
                  ยังไม่มี Match ที่พร้อมสร้าง Queue
                </div>
              )}
              {previewMatches.map((match, index) => (
                <article
                  key={`${match.previewRank}-${index}`}
                  className="rmp-preview-match"
                  style={{
                    padding: 16,
                    border: '1px solid #dbe3ef',
                    borderRadius: 14
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 12
                  }}>
                    <strong>Match ตัวเลือก {index + 1} • Rank {match.previewRank}</strong>
                    <span>
                      {match.requiresRecentEncounterApproval && (
                        <b style={{ color: '#b45309', marginRight: 8 }}>
                          ⚠ ต้อง Approve ประวัติ 2 Match ล่าสุด
                        </b>
                      )}
                      Team gap {match.analysis?.teamGap ?? '-'}
                    </span>
                  </div>

                  <div className="rmp-result-teams">
                    {renderPreviewTeam('Team 1', match.team1 || [], index, 'team1')}
                    <span className="rmp-vs">VS</span>
                    {renderPreviewTeam('Team 2', match.team2 || [], index, 'team2')}
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: 10,
                    marginTop: 14
                  }}>
                    <button
                      type="button"
                      disabled={previewCreatingIndex !== null}
                      onClick={() => swapPreviewPair(index)}
                    >
                      🔄 สลับคู่
                    </button>
                    <button
                      type="button"
                      className="rmp-generate-button"
                      disabled={previewCreatingIndex !== null}
                      onClick={() => createQueueFromPreview(index)}
                    >
                      {previewCreatingIndex === index
                        ? 'กำลังสร้าง Queue...'
                        : 'เลือก Match นี้เพื่อสร้าง Queue'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      ), document.body)}

      {lastResult && (
        <div className="rmp-result">
          <div className="rmp-result-header">
            <strong>
              สร้าง {createdMatches.length} Match สำเร็จ
            </strong>
            <span>
              Match #{createdMatches
                .map(item => item.groupMatchNo || item.matchId)
                .join(', #')}
            </span>
          </div>

          {lastResult.approvedPlayingPlayers?.length > 0 && (
            <div className="rmp-approved-result">
              Approved สถานะอื่น:{' '}
              {lastResult.approvedPlayingPlayers
                .map(player => `${player.name} (${player.status || '-'})`)
                .join(', ')}
            </div>
          )}

          <div className="rmp-result-list">
            {createdMatches.map((result, index) => (
              <article className="rmp-result-match" key={result.matchId || index}>
                <div className="rmp-result-match-heading">
                  <strong>
                    Match #{result.groupMatchNo || result.matchId || index + 1}
                  </strong>
                  <span>Team gap {result.analysis?.teamGap ?? '-'}</span>
                </div>

                <details className="rmp-result-details">
                  <summary>ดูรายละเอียดการจัด Match</summary>
                  <div className="rmp-result-details-body">
                    {result.mode !== 'MANUAL_4' && result.analysis && (
                      <div className="rmp-range-result">
                        ลำดับคัดเลือก:{' '}
                        {result.analysis.sameRank
                          ? `Rank ${result.analysis.ranks?.[0] || '-'} เดียวกัน`
                          : `ขยาย Rank ห่าง ${result.analysis.rankDistance ?? '-'} ระดับ`}
                        {' • '}ช่วงคะแนนที่ใช้ไม่เกิน{' '}
                        {result.analysis.pointExpansionStep ?? 300} คะแนน
                        {' • '}คะแนนสูงสุด–ต่ำสุดห่าง{' '}
                        {result.analysis.playerSpread ?? '-'} คะแนน
                      </div>
                    )}

                    {result.opponentRepeatRelaxed && (
                      <div className="rmp-repeat-result relaxed">
                        ผ่อนคู่ต่อสู้ซ้ำจาก Match ล่าสุดอัตโนมัติ
                        เพราะไม่พบตัวเลือกใหม่ที่ผ่านเงื่อนไข
                      </div>
                    )}

                    {result.rangeExpandedForRepeatAvoidance && (
                      <div className="rmp-repeat-result relaxed">
                        ขยายคะแนน/Rank อัตโนมัติเพื่อหลีกเลี่ยงผู้เล่นซ้ำ
                        โดยใช้ช่วงใกล้ที่สุด {result.effectivePointRange || result.analysis?.pointExpansionStep || '-'} คะแนน
                      </div>
                    )}

                    {result.partnerRepeatOverridden && (
                      <div className="rmp-repeat-result approved">
                        ผู้ใช้ Approve คู่ทีมที่เคยเล่นด้วยกันแล้ว
                      </div>
                    )}

                    {result.analysis?.waitReleasedPartnerPairs?.length > 0 && (
                      <div className="rmp-repeat-result waited">
                        อนุญาตคู่ทีมเดิมเพราะผู้เล่นทั้งคู่รอครบ 30 นาที:{' '}
                        {result.analysis.waitReleasedPartnerPairs
                          .map(pair => `${pair.nameA} + ${pair.nameB}`)
                          .join(', ')}
                      </div>
                    )}

                    {result.mode === 'POINT_RANGE_RANDOM' && (
                      <div className="rmp-range-result">
                        ผู้เล่นอ้างอิง: {result.anchorPlayer?.name || '-'}{' '}
                        ({result.anchorPlayer?.point ?? '-'} คะแนน) • ช่วงไม่เกิน{' '}
                        {result.pointRange} คะแนน
                      </div>
                    )}

                    {(
                      result.mode === 'PICK_ONE' ||
                      result.mode === 'PRIORITY_SELECT'
                    ) && (
                        <div className="rmp-range-result">
                          เทียบรายบุคคล:{' '}
                          {result.team1?.[0]?.name || 'T1-1'} ↔{' '}
                          {result.team2?.[0]?.name || 'T2-1'} ห่าง{' '}
                          {result.analysis?.individualGaps?.[0] ?? '-'} คะแนน
                          {' • '}
                          {result.team1?.[1]?.name || 'T1-2'} ↔{' '}
                          {result.team2?.[1]?.name || 'T2-2'} ห่าง{' '}
                          {result.analysis?.individualGaps?.[1] ?? '-'} คะแนน
                          {' • '}
                          ภายในทีม: T1 ห่าง{' '}
                          {result.analysis?.teammateGaps?.[0] ?? '-'} คะแนน
                          {' / '}T2 ห่าง{' '}
                          {result.analysis?.teammateGaps?.[1] ?? '-'} คะแนน
                          {' • '}
                          {result.rangeExpandedForRepeatAvoidance
                            ? `ระบบขยายอัตโนมัติเพื่อเลี่ยงคนซ้ำ (เดิมกำหนดไม่เกิน ${result.pointRange})`
                            : result.rangeOverridden
                              ? `ผู้ใช้ยืนยันใช้คู่ที่ใกล้ที่สุด (เดิมกำหนดไม่เกิน ${result.pointRange})`
                              : `เกณฑ์ไม่เกิน ${result.pointRange}`}
                        </div>
                      )}

                    {result.mode === 'PRIORITY_SELECT' && (
                      <div className="rmp-queue-team-order">
                        เรียงตามคิว: Team 1 ={' '}
                        {(result.team1 || []).map(player => player.name).join(' + ')}
                        {' • '}Team 2 ={' '}
                        {(result.team2 || []).map(player => player.name).join(' + ')}
                      </div>
                    )}

                    {result.playStyle && result.playStyle !== 'ANY' && (
                      <div className="rmp-result-play-style">
                        Mood ที่ใช้จัด Match: {result.playStyleLabel ||
                          getPlayStyleLabel(result.playStyle)}
                      </div>
                    )}

                    <p className="rmp-reason">{result.reason}</p>
                  </div>
                </details>

                <div className="rmp-result-teams">
                  {renderTeam('Team 1', result.team1 || [])}
                  <span className="rmp-vs">VS</span>
                  {renderTeam('Team 2', result.team2 || [])}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default RandomMatchPanel;
